import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AlertTriangle, ChevronLeft, ChevronRight, Crown, Download, Edit2, Gift, Tag, Trash2, X } from 'lucide-react';
import '../../../shared/components/layout/StoreDashboardLayout.css';
import './StoreAppointments.css';
import { ANALYTICS_ROI_SUMMARY, PROFILE_STORES_OFFERS } from '../../../shared/constants/endpoints';
import DraftNudgeBanner from '../../offers/components/DraftNudgeBanner';
import ErrorBoundary from '../../../shared/components/ErrorBoundary';
import AppointmentSettings from '../components/AppointmentSettings';
import ServiceManager from '../components/ServiceManager';
import BookingCalendar from '../components/BookingCalendar';
import BookingList from '../components/BookingList';
import SlotManager from '../components/SlotManager';
import WalkinBookingModal from '../components/WalkinBookingModal';
import EditWalkinBookingModal from '../components/EditWalkinBookingModal';
import DeleteSlotModal from '../components/DeleteSlotModal';
import useAppointments from '../hooks/useAppointments';
import useDirtySettings from '../hooks/useDirtySettings';
import BookingRulesHeader from '../components/BookingRulesHeader';
import OperatingHoursSection from '../components/OperatingHoursSection';
import ReminderPillGroup, { formatSelectedSubtitle } from '../components/ReminderPillGroup';
import StepperInput from '../components/StepperInput';
import CancellationCallout from '../components/CancellationCallout';
import MultiServiceRow from '../components/MultiServiceRow';
import ServicesHeader from '../components/ServicesHeader';
import ServiceTileHead from '../components/ServiceTileHead';
import CapacityTimeline from '../components/CapacityTimeline';
import ServiceTileSummary from '../components/ServiceTileSummary';
// Mock data import removed - using real API data only
import EmptyState from '../../../shared/components/ui/EmptyState';
import Skeleton from '../../../shared/components/ui/Skeleton';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog';
import { resolveBookingsRequest } from '../utils/resolveBookingsWindow';
import { earliestMerchantPastDate, earliestMerchantPastDateIso, monthsAgoIso, DEFAULT_HISTORY_MONTHS } from '../../../shared/utils/merchantHistoryWindow';
import { formatBookingServices, bookingServicesSearchText } from '../utils/bookingServices';
import { bookingsToCsvRows, bookingHistoryFilename } from '../utils/bookingHistoryCsv';
import { downloadCsv } from '../../analytics/utils/csvExport';
import {
  BOOKING_STATUS_TRANSITIONS,
  APPOINTMENT_STATUS_SORT_ORDER,
  normalizeBookingStatus,
  bookingStatusLabel as statusBadgeLabel,
} from '../utils/bookingStatus';

const DEFAULT_SETTINGS = {
  bookingEnabled: true,
  maxServicesAllowed: 3,
  multiServiceBookingsEnabled: true,
  timezone: 'Asia/Kolkata',
  reminderOffsets: '2h,30m',
  weekdayOpen: '09:00',
  weekdayClose: '21:00',
  weekendOpen: '10:00',
  weekendClose: '22:00',
  rescheduleWindowHours: 4,
  cancellationWindowHours: 4
};

const EMPTY_SERVICE_FORM = {
  serviceName: '',
  serviceDescription: '',
  appointmentMode: 'IN_STORE',
  appointmentDurationMins: 30,
  maxBookingsPerSlot: 1,
  depositAmount: ''
};

const REMINDER_OPTIONS = [
  { key: '24h', label: '1 day', recommended: true },
  { key: '12h', label: '12 hours', recommended: false },
  { key: '4h', label: '4 hours', recommended: false },
  { key: '2h', label: '2 hours', recommended: true },
  { key: '1h', label: '1 hour', recommended: true },
  { key: '30m', label: '30 min', recommended: true }
];

const WEEKDAY_CHIPS = [
  { key: 'mon', label: 'Mon', index: 1 },
  { key: 'tue', label: 'Tue', index: 2 },
  { key: 'wed', label: 'Wed', index: 3 },
  { key: 'thu', label: 'Thu', index: 4 },
  { key: 'fri', label: 'Fri', index: 5 },
  { key: 'sat', label: 'Sat', index: 6 },
  { key: 'sun', label: 'Sun', index: 0 }
];

const SERVICE_HOLD_TYPES = [
  { key: 'daily_full', label: 'Daily halt' },
  { key: 'weekly_full', label: 'Weekday halt' },
  { key: 'time_window', label: 'Time-window halt' }
];

const SERVICE_HOLD_TYPE_HELP = {
  daily_full: 'Blocks this service for the full day, every day.',
  weekly_full: 'Blocks this service for the full day on selected weekdays.',
  time_window: 'Blocks only selected hours on selected weekdays.'
};

const SERVICE_HOLD_REASON_MAX_LENGTH = 50;

const HOLD_DAY_INDEXES = WEEKDAY_CHIPS.map((day) => day.index);

const FILL_COLORS = ['var(--trae-accent)', 'var(--trae-accent-cyan)', 'var(--trae-accent-strong)'];

const AppointmentsLoadingSkeleton = () => (
  <section className="appointments-v2-section appointments-v2-skeleton-shell" aria-label="Loading appointments">
    <div className="appointments-v2-kpi-grid">
      {Array.from({ length: 5 }).map((_, index) => (
        <article key={`appointments-kpi-skeleton-${index}`} className="appointments-v2-kpi-card">
          <Skeleton height={12} width="55%" />
          <Skeleton height={30} width="45%" />
          <Skeleton height={12} width="70%" />
        </article>
      ))}
    </div>
    <article className="appointments-v2-card appointments-v2-bookings-card">
      <div className="appointments-v2-card-header">
        <Skeleton height={18} width={180} />
      </div>
      <div className="appointments-v2-card-body">
        <Skeleton height={36} width="100%" />
        <Skeleton height={36} width="100%" />
        <Skeleton height={36} width="100%" />
        <Skeleton height={36} width="100%" />
      </div>
    </article>
  </section>
);

const parseReminderOffsets = (value) => new Set(
  String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
);

const formatReminderOffsets = (values) => Array.from(values).join(',');

// Zopeo operates entirely in Asia/Kolkata (see ZopeoConstants.APP_ZONE_ID on the
// backend). Appointment settings must persist this fixed zone, not the merchant's
// browser zone, so slot generation stays consistent regardless of who edits it.
const APP_TIMEZONE = 'Asia/Kolkata';
const detectBrowserTimezone = () => APP_TIMEZONE;

/**
 * Multi-service awareness: a booking can have several services attached via
 * `booking.services[].serviceUuid` (B4 response shape). The legacy single
 * `booking.serviceUuid` is the primary one used by capacity / slot lookups.
 * To decide whether a booking should surface on a given service tile we need
 * to check BOTH — otherwise multi-service walk-ins only ever appear on their
 * primary service's timeline.
 */
const bookingIncludesService = (booking, serviceUuid) => {
  if (!booking || !serviceUuid) return false;
  if (booking.serviceUuid === serviceUuid) return true;
  if (Array.isArray(booking.services)) {
    return booking.services.some((s) => s?.serviceUuid === serviceUuid);
  }
  return false;
};

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }
  const parsed = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
};

const pad = (value) => String(value).padStart(2, '0');

const formatDateInput = (value = new Date()) => {
  const parsed = parseDateValue(value);
  if (!parsed) {
    return '';
  }
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
};

const getTodayDateKey = () => formatDateInput(new Date());

const extractDateKey = (value) => {
  if (typeof value === 'string') {
    const matched = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (matched?.[1]) {
      return matched[1];
    }
  }
  return formatDateInput(value);
};

// parseDateKey and toLocalDateTimeString removed - were only used for mock data

// buildMockBookingsForDate function removed - using real API data only

const formatTime = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) {
    return '--';
  }
  const valueText = parsed.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  return valueText.replace(/\b(am|pm)\b/i, (matched) => matched.toUpperCase());
};

const normalizeSearchText = (value) => String(value || '').trim().toLowerCase();

const normalizeDigits = (value) => String(value || '').replace(/\D/g, '');

const fuzzySubsequenceMatch = (needle, haystack) => {
  if (!needle) {
    return true;
  }
  if (!haystack) {
    return false;
  }
  let needleIndex = 0;
  for (let haystackIndex = 0; haystackIndex < haystack.length; haystackIndex += 1) {
    if (haystack[haystackIndex] === needle[needleIndex]) {
      needleIndex += 1;
      if (needleIndex >= needle.length) {
        return true;
      }
    }
  }
  return false;
};

const bookingMatchesSearch = (booking, query) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) {
    return true;
  }

  const normalizedName = normalizeSearchText(booking?.customerName);
  const normalizedPhone = normalizeDigits(booking?.customerPhone);
  const queryDigits = normalizeDigits(normalizedQuery);

  if (normalizedName.includes(normalizedQuery)) {
    return true;
  }
  if (fuzzySubsequenceMatch(normalizedQuery.replace(/\s+/g, ''), normalizedName.replace(/\s+/g, ''))) {
    return true;
  }
  if (queryDigits) {
    return normalizedPhone.includes(queryDigits) || fuzzySubsequenceMatch(queryDigits, normalizedPhone);
  }
  return false;
};

const resolveRelativeOffsetTop = (element, ancestor) => {
  if (!element || !ancestor) {
    return 0;
  }
  let current = element;
  let offset = 0;
  while (current && current !== ancestor) {
    offset += current.offsetTop || 0;
    current = current.offsetParent;
  }
  return offset;
};

const formatCurrencyFromPaise = (value) => {
  if (!Number.isFinite(Number(value))) {
    return '—';
  }
  return `₹${Math.round(Number(value) / 100).toLocaleString('en-IN')}`;
};

const formatMembershipLabel = (details) => {
  if (!details || details.membershipAvailable !== true) {
    return 'Not available';
  }
  return details.membershipName || 'Available';
};

/**
 * V2 — offer chip on booking rows. RELEASED means the claim was returned
 * (booking cancelled / no-show elsewhere): shown struck-through so the
 * merchant knows the discount is no longer owed.
 */
const AppointmentOfferChip = ({ booking }) => {
  if (booking?.offerId == null && !booking?.offerCode) {
    return null;
  }
  const released = booking?.offerRedemptionStatus === 'RELEASED';
  const label = booking?.offerTitle || booking?.offerCode || 'Offer';
  const discount = Number(booking?.discountPaise);
  const hasDiscount = Number.isFinite(discount) && discount > 0;
  return (
    <span
      className={`appointments-v2-offer-chip${released ? ' released' : ''}`}
      title={released ? 'Offer claim released — discount no longer applies' : 'Offer applied to this booking'}
    >
      <Tag size={11} />
      <span className="appointments-v2-offer-chip-label">{label}</span>
      {hasDiscount ? <strong>−{formatCurrencyFromPaise(discount)}</strong> : null}
    </span>
  );
};

// CP-101 — reliability band → human label. HIGH earns guaranteed-slot standing.
const formatReliabilityLabel = (reliability) => {
  const band = String(reliability?.band || 'NEW').toUpperCase();
  if (band === 'HIGH') return 'Reliable · guaranteed slot';
  if (band === 'MEDIUM') return 'Usually shows up';
  if (band === 'LOW') return 'No-show risk';
  return 'New customer';
};

const formatReliabilityDetail = (reliability) => {
  const kept = Number(reliability?.kept || 0);
  const noShows = Number(reliability?.noShows || 0);
  const lateCancels = Number(reliability?.lateCancels || 0);
  const parts = [`${kept} kept`];
  if (noShows > 0) parts.push(`${noShows} no-show${noShows === 1 ? '' : 's'}`);
  if (lateCancels > 0) parts.push(`${lateCancels} late cancel${lateCancels === 1 ? '' : 's'}`);
  return parts.join(' · ');
};

/** Membership benefit attached to the visit — the member counterpart of the offer chip. */
const AppointmentMembershipChip = ({ booking }) => {
  if (!booking?.membershipUuid) {
    return null;
  }
  const label = booking?.membershipBenefitLabel || 'Membership benefit';
  const value = Number(booking?.membershipBenefitPaise);
  const hasValue = Number.isFinite(value) && value > 0;
  return (
    <span
      className="appointments-v2-offer-chip appointments-v2-membership-chip"
      title="Membership benefit applied to this visit"
    >
      <Crown size={11} />
      <span className="appointments-v2-offer-chip-label">{label}</span>
      {hasValue ? <strong>−{formatCurrencyFromPaise(value)}</strong> : null}
    </span>
  );
};

/** CP-111 (A2): loyalty reward attached to the visit — the third benefit chip. */
const AppointmentLoyaltyRewardChip = ({ booking }) => {
  if (!booking?.loyaltyRewardUuid) {
    return null;
  }
  const label = booking?.loyaltyRewardLabel || 'Loyalty reward';
  const value = Number(booking?.loyaltyRewardPaise);
  const hasValue = Number.isFinite(value) && value > 0;
  return (
    <span
      className="appointments-v2-offer-chip appointments-v2-membership-chip"
      title="Loyalty reward applied to this visit"
    >
      <Gift size={11} />
      <span className="appointments-v2-offer-chip-label">{label}</span>
      {hasValue ? <strong>−{formatCurrencyFromPaise(value)}</strong> : null}
    </span>
  );
};

const AppointmentDetailsModal = ({
  isOpen,
  details,
  summary,
  isLoading,
  onClose
}) => {
  if (!isOpen) {
    return null;
  }

  const services = Array.isArray(details?.services) ? details.services : [];
  const lastVisitServices = Array.isArray(details?.lastVisitServices) ? details.lastVisitServices : [];
  const customerName = details?.customerName || summary?.customerName || 'Customer';
  const customerPhone = details?.customerPhone || summary?.customerPhone || 'No phone added';

  return (
    <div className="zone-modal-overlay" onClick={onClose}>
      <div className="zone-modal appointments-v2-booking-detail-modal" onClick={(event) => event.stopPropagation()}>
        <div className="zone-modal-header appointments-v2-booking-detail-header">
          <div>
            <p className="appointments-v2-booking-detail-kicker">Appointment details</p>
            <h3>{customerName}</h3>
            <span>{customerPhone}</span>
          </div>
          <button type="button" className="appointments-v2-btn appointments-v2-btn-ghost appointments-v2-btn-tiny" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="zone-modal-body appointments-v2-booking-detail-body">
          {isLoading ? (
            <div className="appointments-v2-booking-detail-loading">
              <Skeleton height={18} width="60%" />
              <Skeleton height={14} width="40%" />
              <Skeleton height={72} width="100%" />
            </div>
          ) : (
            <>
              <div className="appointments-v2-booking-detail-grid">
                <article className="appointments-v2-booking-detail-stat">
                  <span className="appointments-v2-booking-detail-label">Estimated billing</span>
                  <strong>{formatCurrencyFromPaise(details?.estimatedBillingPaise)}</strong>
                </article>
                <article className="appointments-v2-booking-detail-stat">
                  <span className="appointments-v2-booking-detail-label">Membership</span>
                  <strong>{formatMembershipLabel(details)}</strong>
                </article>
                <article className="appointments-v2-booking-detail-stat">
                  <span className="appointments-v2-booking-detail-label">Visits to this store</span>
                  <strong>{Number.isFinite(Number(details?.visitCount)) ? details.visitCount : '—'}</strong>
                </article>
                {/* CP-101 — platform-wide reliability: risk visible before the slot is at stake. */}
                {details?.customerReliability ? (
                  <article className="appointments-v2-booking-detail-stat">
                    <span className="appointments-v2-booking-detail-label">Reliability</span>
                    <strong className={`appointments-v2-reliability-${String(details.customerReliability.band || 'NEW').toLowerCase()}`}>
                      {formatReliabilityLabel(details.customerReliability)}
                    </strong>
                    <span className="appointments-v2-booking-detail-sub">
                      {formatReliabilityDetail(details.customerReliability)}
                    </span>
                  </article>
                ) : null}
                {(details?.offerId != null || details?.offerCode) && (
                  <article className="appointments-v2-booking-detail-stat">
                    <span className="appointments-v2-booking-detail-label">Offer applied</span>
                    <strong className={details?.offerRedemptionStatus === 'RELEASED' ? 'appointments-v2-offer-detail-released' : undefined}>
                      {details?.offerTitle || details?.offerCode || 'Offer'}
                    </strong>
                    <span className="appointments-v2-booking-detail-sub">
                      {Number.isFinite(Number(details?.discountPaise)) && Number(details.discountPaise) > 0
                        ? `−${formatCurrencyFromPaise(details.discountPaise)}`
                        : ''}
                      {details?.offerCode && details?.offerTitle ? ` · ${details.offerCode}` : ''}
                      {details?.offerRedemptionStatus === 'RELEASED' ? ' · released — no longer applies' : ''}
                    </span>
                  </article>
                )}
              </div>

              {/* S2 — server-computed visit summary: same numbers the customer's app shows. */}
              {details?.visitSummary ? (
                <section className="appointments-v2-booking-detail-section">
                  <h4>
                    {String(details.visitSummary.status || '').toUpperCase() === 'COMPLETED'
                      ? 'Final bill'
                      : 'Estimated bill'}
                  </h4>
                  <div className="appointments-v2-bill-card">
                    {(details.visitSummary.services || []).map((line, index) => (
                      <div
                        className="appointments-v2-bill-row"
                        key={`${line?.serviceUuid || line?.serviceName || 'line'}-${index}`}
                      >
                        <span className="appointments-v2-bill-label">{line?.serviceName || 'Service'}</span>
                        <span className="appointments-v2-bill-value">
                          {formatCurrencyFromPaise(line?.priceAtBookingPaise)}
                        </span>
                      </div>
                    ))}

                    {details.visitSummary.offer ? (
                      <div className={`appointments-v2-bill-row appointments-v2-bill-discount${details.visitSummary.offer.redemptionStatus === 'RELEASED' ? ' released' : ''}`}>
                        <span className="appointments-v2-bill-label">
                          {details.visitSummary.offer.title || 'Offer'}
                          {details.visitSummary.offer.code ? ` · ${details.visitSummary.offer.code}` : ''}
                        </span>
                        <span className="appointments-v2-bill-value">
                          {details.visitSummary.offer.redemptionStatus === 'RELEASED'
                            ? 'Not applied'
                            : `−${formatCurrencyFromPaise(details.visitSummary.offer.discountPaise)}`}
                        </span>
                      </div>
                    ) : null}

                    {details.visitSummary.membershipBenefit ? (
                      <div className="appointments-v2-bill-row appointments-v2-bill-discount">
                        <span className="appointments-v2-bill-label">
                          {details.visitSummary.membershipBenefit.label || 'Member benefit'}
                        </span>
                        <span className="appointments-v2-bill-value">
                          {details.visitSummary.membershipBenefit.valuePaise > 0
                            ? `−${formatCurrencyFromPaise(details.visitSummary.membershipBenefit.valuePaise)}`
                            : 'Member'}
                        </span>
                      </div>
                    ) : null}

                    <div className="appointments-v2-bill-row appointments-v2-bill-muted-row">
                      <span className="appointments-v2-bill-label">Taxes</span>
                      <span className="appointments-v2-bill-muted">
                        {details.visitSummary.taxNote ? 'At store' : 'Included'}
                      </span>
                    </div>

                    <div className="appointments-v2-bill-divider" />

                    <div className="appointments-v2-bill-row appointments-v2-bill-total-row">
                      <span className="appointments-v2-bill-total-label">
                        Total payable
                        <span className="appointments-v2-bill-total-sub">Pay at store</span>
                      </span>
                      <span className="appointments-v2-bill-total-value">
                        {formatCurrencyFromPaise(details.visitSummary.totalPaise)}
                      </span>
                    </div>

                    {details.visitSummary.taxNote ? (
                      <p className="appointments-v2-bill-tax-note">
                        Prices exclude taxes — applicable GST is added at store billing.
                      </p>
                    ) : null}
                  </div>
                </section>
              ) : (
                <section className="appointments-v2-booking-detail-section">
                  <h4>Services chosen</h4>
                  {services.length > 0 ? (
                    <div className="appointments-v2-booking-detail-pill-list">
                      {services.map((service, index) => (
                        <span key={`${service?.serviceUuid || service?.serviceName || 'service'}-${index}`} className="appointments-v2-booking-detail-pill">
                          {service?.serviceName || 'Service'}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p>No services available.</p>
                  )}
                </section>
              )}

              <section className="appointments-v2-booking-detail-section">
                <h4>Last visit services</h4>
                {lastVisitServices.length > 0 ? (
                  <div className="appointments-v2-booking-detail-pill-list">
                    {lastVisitServices.map((service, index) => (
                      <span key={`${service}-${index}`} className="appointments-v2-booking-detail-pill appointments-v2-booking-detail-pill-muted">
                        {service}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p>No previous visit services found.</p>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const buildServiceSlotSummaryViewModel = (summary) => {
  if (!summary || !summary.status) {
    return null;
  }
  const status = String(summary.status || '').toUpperCase();
  const availableCount = Number(summary.availableSlotCount ?? 0);
  const totalCount = Number(summary.totalSlotCount ?? 0);
  const bookedCount = Number(summary.bookedSlotCount ?? 0);
  const nextSlot = formatTime(summary.nextAvailableSlot);

  if (status === 'AVAILABLE') {
    return {
      status,
      tone: 'open',
      primary: `Today: ${Math.max(0, availableCount)} open`,
      secondary: `Next: ${nextSlot}`,
      footer: `${totalCount} slots · ${bookedCount} booked · ${Math.max(0, availableCount)} open`
    };
  }

  const unavailableLabels = {
    SERVICE_INACTIVE: 'Service inactive',
    CLOSED_TODAY: 'Closed today',
    BLOCKED_TODAY: 'Blocked today',
    FULLY_BOOKED: 'Fully booked today',
    NO_SAME_DAY_SLOTS: 'No same-day slots'
  };
  const primary = unavailableLabels[status] || 'Availability unavailable';
  const secondary = summary.reason || 'No bookable slots for this date';

  return {
    status,
    tone: status === 'FULLY_BOOKED' ? 'booked' : 'muted',
    primary,
    secondary,
    footer: `${primary} · ${Math.max(0, availableCount)} open`
  };
};

const parseSlotDateTime = (value) => {
  const parsed = parseDateValue(value);
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

const resolveServiceUuid = (serviceOrUuid) => (
  typeof serviceOrUuid === 'string' ? serviceOrUuid : serviceOrUuid?.serviceUuid
);

const readFiniteNumber = (...values) => {
  for (let index = 0; index < values.length; index += 1) {
    const parsed = Number(values[index]);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

export const doesBookingOverlapSlot = (booking, slot, serviceOrUuid) => {
  const serviceUuid = resolveServiceUuid(serviceOrUuid) || slot?.serviceUuid;
  if (!booking || !slot || !serviceUuid || booking.serviceUuid !== serviceUuid) {
    return false;
  }

  const bookingStart = parseSlotDateTime(booking.appointmentStartTime);
  const bookingEnd = parseSlotDateTime(booking.appointmentEndTime);
  const slotStart = parseSlotDateTime(slot.slotStartTime);
  const slotEnd = parseSlotDateTime(slot.slotEndTime);

  if (!bookingStart || !bookingEnd || !slotStart || !slotEnd) {
    return false;
  }

  return bookingStart < slotEnd && bookingEnd > slotStart;
};

export const buildServiceSlotChipViewModels = (serviceOrUuid, slots, bookings = []) => {
  const serviceUuid = resolveServiceUuid(serviceOrUuid);
  if (!serviceUuid || !Array.isArray(slots)) {
    return [];
  }

  return slots.map((slot, index) => {
    const matchingBookings = Array.isArray(bookings)
      ? bookings.filter((booking) => doesBookingOverlapSlot(booking, slot, serviceUuid))
      : [];
    const capacity = readFiniteNumber(
      slot?.capacity,
      slot?.maxBookingsPerSlot,
      slot?.capacityPerSlot,
      serviceOrUuid?.maxBookingsPerSlot,
      serviceOrUuid?.capacityPerSlot
    );
    const vacanciesLeft = readFiniteNumber(slot?.vacanciesLeft);
    const bookedCount = readFiniteNumber(slot?.bookedCount, matchingBookings.length) || 0;
    const hasCapacity = capacity !== null && capacity > 0;
    const hasVacancies = vacanciesLeft !== null;
    let status = 'open';

    if (slot?.available === false) {
      status = 'unavailable';
    } else if ((hasVacancies && vacanciesLeft <= 0) || (hasCapacity && bookedCount >= capacity)) {
      status = 'full';
    } else if (bookedCount > 0 || (hasCapacity && hasVacancies && vacanciesLeft < capacity)) {
      status = 'partial';
    }

    return {
      key: `${serviceUuid}-${slot?.slotStartTime || index}`,
      serviceUuid,
      slot,
      status,
      tone: status === 'full' ? 'booked' : status === 'unavailable' ? 'muted' : status,
      label: `${formatTime(slot?.slotStartTime)}-${formatTime(slot?.slotEndTime)}`,
      startLabel: formatTime(slot?.slotStartTime),
      endLabel: formatTime(slot?.slotEndTime),
      bookedCount,
      capacity,
      vacanciesLeft,
      matchingBookings
    };
  });
};

export const buildServiceSlotPreviewViewModel = ({
  service,
  slots,
  bookings,
  summary,
  maxVisibleSlots = 5
} = {}) => {
  const summaryView = buildServiceSlotSummaryViewModel(summary);
  const slotChips = buildServiceSlotChipViewModels(service, slots, bookings);
  const visibleCount = Math.max(0, Number(maxVisibleSlots) || 0);
  const visibleSlotChips = slotChips.slice(0, visibleCount);

  return {
    summaryView,
    slotChips: visibleSlotChips,
    hiddenCount: Math.max(0, slotChips.length - visibleSlotChips.length),
    isEmpty: !summaryView && slotChips.length === 0,
    totalSlotCount: slotChips.length
  };
};

export const buildServiceSlotDrilldownViewModel = ({ selectedSlot, bookings = [] } = {}) => {
  if (!selectedSlot?.serviceUuid || !selectedSlot?.slot) {
    return null;
  }

  const [slotChip] = buildServiceSlotChipViewModels(selectedSlot, [selectedSlot.slot], bookings);
  if (!slotChip) {
    return null;
  }

  const sortedBookings = [...slotChip.matchingBookings].sort((leftBooking, rightBooking) => (
    (parseSlotDateTime(leftBooking?.appointmentStartTime)?.getTime() || 0)
    - (parseSlotDateTime(rightBooking?.appointmentStartTime)?.getTime() || 0)
  ));

  return {
    serviceUuid: selectedSlot.serviceUuid,
    serviceName: selectedSlot.serviceName || 'Service',
    slotLabel: slotChip.label,
    startLabel: slotChip.startLabel,
    endLabel: slotChip.endLabel,
    status: slotChip.status,
    tone: slotChip.tone,
    bookingCount: sortedBookings.length,
    isEmpty: sortedBookings.length === 0,
    bookings: sortedBookings.map((booking, index) => {
      const normalizedStatus = normalizeBookingStatus(booking?.status);
      return {
        key: booking?.bookingUuid || booking?.appointmentUuid || `${selectedSlot.serviceUuid}-${index}`,
        bookingUuid: booking?.bookingUuid,
        customerName: booking?.customerName || 'Customer',
        customerPhone: booking?.customerPhone || '',
        timeLabel: `${formatTime(booking?.appointmentStartTime)}-${formatTime(booking?.appointmentEndTime)}`,
        appointmentStartTime: booking?.appointmentStartTime,
        serviceUuid: booking?.serviceUuid,
        status: normalizedStatus,
        statusLabel: statusBadgeLabel(normalizedStatus),
        notes: booking?.notes || '',
        walkin: booking?.walkin,
        // V2 — offer snapshot so the drilldown card can show the discount owed.
        offerId: booking?.offerId,
        offerTitle: booking?.offerTitle,
        offerCode: booking?.offerCode,
        discountPaise: booking?.discountPaise,
        offerRedemptionStatus: booking?.offerRedemptionStatus,
        // Membership benefit snapshot (offer XOR membership per visit).
        membershipUuid: booking?.membershipUuid,
        membershipBenefitLabel: booking?.membershipBenefitLabel,
        membershipBenefitPaise: booking?.membershipBenefitPaise,
        // CP-111 (A2): loyalty reward snapshot (third benefit source).
        loyaltyRewardUuid: booking?.loyaltyRewardUuid,
        loyaltyRewardLabel: booking?.loyaltyRewardLabel,
        loyaltyRewardPaise: booking?.loyaltyRewardPaise
      };
    })
  };
};

const formatDateLabel = (value, timezone) => {
  const parsed = parseDateValue(value);
  if (!parsed) {
    return '';
  }
  try {
    return parsed.toLocaleDateString('en-IN', {
      weekday: 'long',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: timezone || undefined
    });
  } catch (_error) {
    return parsed.toLocaleDateString('en-IN', {
      weekday: 'long',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }
};

const addDays = (value, days) => {
  const parsed = parseDateValue(value) || new Date();
  const next = new Date(parsed.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const resolveTimelineBucket = (value) => {
  const date = parseDateValue(value);
  if (!date) {
    return 'all';
  }
  const hour = date.getHours();
  if (hour >= 6 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 22) return 'evening';
  return 'night';
};

const parseTimeToMinutes = (value) => {
  const matched = String(value || '').trim().match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!matched) {
    return null;
  }
  return (Number(matched[1]) * 60) + Number(matched[2]);
};

const isFromAfterTo = (fromTime, toTime) => {
  const fromMinutes = parseTimeToMinutes(fromTime);
  const toMinutes = parseTimeToMinutes(toTime);
  if (fromMinutes === null || toMinutes === null) {
    return false;
  }
  return fromMinutes > toMinutes;
};

const isWithinCustomTimeline = (appointmentTime, startTime, endTime) => {
  const parsedDate = parseDateValue(appointmentTime);
  if (!parsedDate) {
    return false;
  }
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);
  if (startMinutes === null || endMinutes === null) {
    return true;
  }
  if (startMinutes > endMinutes) {
    return false;
  }
  const appointmentMinutes = (parsedDate.getHours() * 60) + parsedDate.getMinutes();
  return appointmentMinutes >= startMinutes && appointmentMinutes <= endMinutes;
};

const normalizeScheduleHoldType = (value) => {
  const matchedType = SERVICE_HOLD_TYPES.find((typeOption) => typeOption.key === value);
  return matchedType?.key || 'daily_full';
};

const normalizeScheduleHoldDays = (days) => {
  const asArray = Array.isArray(days) ? days : [];
  const daySet = new Set(
    asArray
      .map((entry) => Number(entry))
      .filter((entry) => HOLD_DAY_INDEXES.includes(entry))
  );
  return HOLD_DAY_INDEXES.filter((dayIndex) => daySet.has(dayIndex));
};

const buildEmptyServiceHoldDraft = () => ({
  type: 'time_window',
  scope: 'date',
  date: getTodayDateKey(),
  days: [],
  fromTime: '',
  toTime: '',
  reason: ''
});

const normalizeHoldReason = (value) => String(value || '').trim().slice(0, SERVICE_HOLD_REASON_MAX_LENGTH);

const createScheduleHoldId = () => `hold-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const normalizeServiceScheduleHold = (hold, fallbackId = '') => {
  const backendDays = (() => {
    if (Array.isArray(hold?.days)) {
      return hold.days;
    }
    if (typeof hold?.daysJson === 'string') {
      try {
        const parsed = JSON.parse(hold.daysJson);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_error) {
        return [];
      }
    }
    return [];
  })();
  const normalizedType = normalizeScheduleHoldType(hold?.type || hold?.holdType);
  const startDate = typeof hold?.startDate === 'string' ? hold.startDate : '';
  const endDate = typeof hold?.endDate === 'string' ? hold.endDate : '';
  const requestedDate = typeof hold?.date === 'string' ? hold.date : '';
  const dateScoped = Boolean((startDate && endDate && startDate === endDate) || hold?.scope === 'date');
  const scopedDate = dateScoped ? (startDate || requestedDate) : '';
  return {
    id: String(hold?.id || hold?.holdUuid || fallbackId || createScheduleHoldId()),
    holdUuid: hold?.holdUuid || null,
    type: normalizedType,
    scope: dateScoped ? 'date' : 'recurring',
    date: scopedDate,
    startDate: dateScoped ? scopedDate : startDate,
    endDate: dateScoped ? scopedDate : endDate,
    days: normalizeScheduleHoldDays(backendDays),
    fromTime: normalizedType === 'time_window' ? normalizeTimeInputValue(hold?.fromTime) : '',
    toTime: normalizedType === 'time_window' ? normalizeTimeInputValue(hold?.toTime) : '',
    reason: normalizeHoldReason(hold?.reason)
  };
};

const normalizeServiceScheduleHolds = (holds) => {
  if (!Array.isArray(holds)) {
    return [];
  }
  return holds.map((hold, index) => normalizeServiceScheduleHold(hold, `hold-${index + 1}`));
};

const getDayIndexFromDateKey = (dateKey) => {
  const matched = String(dateKey || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!matched) {
    return null;
  }
  const year = Number(matched[1]);
  const month = Number(matched[2]);
  const day = Number(matched[3]);
  const parsed = new Date(year, month - 1, day);
  if (
    Number.isNaN(parsed.getTime())
    || parsed.getFullYear() !== year
    || parsed.getMonth() !== month - 1
    || parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed.getDay();
};

const normalizeHoldDaysForPayload = (hold) => {
  const normalized = normalizeServiceScheduleHold(hold);
  if (normalized.scope === 'date') {
    const dayIndex = getDayIndexFromDateKey(normalized.date);
    return dayIndex === null ? [] : [dayIndex];
  }
  if (normalized.type === 'daily_full') {
    return HOLD_DAY_INDEXES;
  }
  return normalized.days;
};

const isValidNonNegativeAmount = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return true;
  }
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
    return false;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0;
};

const getScheduleHoldValidationError = (hold, options = {}) => {
  const normalized = normalizeServiceScheduleHold(hold);
  const enforceTodayOnly = options.enforceTodayOnly === true;
  const todayDateKey = options.todayDateKey || getTodayDateKey();
  if (enforceTodayOnly && normalized.type !== 'time_window') {
    return 'Holds require a start and end time.';
  }
  if (enforceTodayOnly && normalized.date !== todayDateKey) {
    return 'Holds can only be created for today.';
  }
  if (normalized.scope === 'date' && !normalized.date) {
    return 'Select a date for this hold.';
  }
  if (normalized.scope !== 'date' && normalized.type === 'weekly_full' && normalized.days.length === 0) {
    return 'Select at least one day for weekday halt.';
  }
  if (normalized.type === 'time_window') {
    if (normalized.scope !== 'date' && normalized.days.length === 0) {
      return 'Select at least one day for time-window halt.';
    }
    if (!normalized.fromTime || !normalized.toTime) {
      return 'Select both from and to time for time-window halt.';
    }
    if (normalized.fromTime === normalized.toTime) {
      return 'Hold to time must be later than hold from time.';
    }
    if (isFromAfterTo(normalized.fromTime, normalized.toTime)) {
      return 'Hold from time cannot be later than hold to time.';
    }
    const openMinutes = parseTimeToMinutes(options.appointmentOpenTime);
    const closeMinutes = parseTimeToMinutes(options.appointmentCloseTime);
    const fromMinutes = parseTimeToMinutes(normalized.fromTime);
    const toMinutes = parseTimeToMinutes(normalized.toTime);
    if (
      openMinutes !== null
      && closeMinutes !== null
      && fromMinutes !== null
      && toMinutes !== null
      && (fromMinutes < openMinutes || toMinutes > closeMinutes)
    ) {
      return `Hold time must be between ${options.appointmentOpenTime} and ${options.appointmentCloseTime}.`;
    }
  }
  return '';
};

const getScheduleHoldsValidationError = (holds) => {
  const normalizedHolds = normalizeServiceScheduleHolds(holds);
  for (let index = 0; index < normalizedHolds.length; index += 1) {
    const holdError = getScheduleHoldValidationError(normalizedHolds[index]);
    if (holdError) {
      return holdError;
    }
  }
  return '';
};

const getServiceDraftValidationError = ({ draft, isVirtualService }) => {
  const durationValue = Number(draft?.durationMinutes);
  if (!Number.isFinite(durationValue) || durationValue <= 0) {
    return 'Select a valid service duration.';
  }

  const capacityValue = Number(draft?.capacityPerSlot);
  if (!Number.isFinite(capacityValue) || capacityValue <= 0) {
    return 'Select a valid slot capacity.';
  }

  const openMinutes = parseTimeToMinutes(draft?.openTime);
  const closeMinutes = parseTimeToMinutes(draft?.closeTime);
  if (openMinutes === null || closeMinutes === null) {
    return 'Select both opening and closing time.';
  }
  if (openMinutes > closeMinutes) {
    return 'Opening time cannot be later than closing time.';
  }

  const activeDays = normalizeScheduleHoldDays(draft?.activeDays);
  if (activeDays.length === 0) {
    return 'Select at least one operating day.';
  }

  if (!isVirtualService) {
    const lunchTime = normalizeTimeInputValue(draft?.extraField);
    if (lunchTime) {
      const lunchMinutes = parseTimeToMinutes(lunchTime);
      if (lunchMinutes === null) {
        return 'Select a valid lunch break time.';
      }
      if (lunchMinutes < openMinutes || lunchMinutes > closeMinutes) {
        return 'Lunch break must be within operating hours.';
      }
    }
  }

  const amountInput = isVirtualService
    ? (draft?.extraField ?? draft?.price)
    : draft?.price;
  if (!isValidNonNegativeAmount(amountInput)) {
    return isVirtualService
      ? 'Deposit must be a valid non-negative amount.'
      : 'Price must be a valid non-negative amount.';
  }

  const scheduleHoldsError = getScheduleHoldsValidationError(draft?.scheduleHolds);
  if (scheduleHoldsError) {
    return scheduleHoldsError;
  }

  return '';
};

const formatScheduleHoldTimeDetail = (hold) => {
  const normalized = normalizeServiceScheduleHold(hold);
  const scopeText = normalized.scope === 'date' && normalized.date ? ` on ${normalized.date}` : '';
  if (normalized.type !== 'time_window') {
    return `All operating hours${scopeText}`;
  }
  if (!normalized.fromTime || !normalized.toTime) {
    return 'Time not set';
  }
  return `${normalized.fromTime} to ${normalized.toTime}${scopeText}`;
};

const normalizeTimeInputValue = (value) => {
  const matched = String(value || '').match(/([01]\d|2[0-3]):([0-5]\d)/);
  if (!matched) {
    return '';
  }
  return `${matched[1]}:${matched[2]}`;
};

const TimePickerControl = ({
  value,
  onChange,
  ariaLabel,
  className = '',
  disabled = false
}) => {
  return (
    <div className={`appointments-v2-time-control ${className}`.trim()}>
      <input
        type="time"
        className="time-input appointments-v2-time-input"
        value={normalizeTimeInputValue(value)}
        onChange={onChange}
        aria-label={ariaLabel || 'Select time'}
        disabled={disabled}
      />
    </div>
  );
};

const getDurationMinutes = (booking, serviceMap) => {
  const start = parseDateValue(booking?.appointmentStartTime);
  const end = parseDateValue(booking?.appointmentEndTime);
  if (start && end && end.getTime() > start.getTime()) {
    return Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000));
  }
  const mappedService = serviceMap.get(booking?.serviceUuid);
  return Number(mappedService?.appointmentDurationMins) || 30;
};

const DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const MiniCalendar = ({ selectedDate, onDateSelect, bookingDates = new Set() }) => {
  const [viewMonth, setViewMonth] = useState(() => {
    const d = selectedDate || new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }, []);

  // Merchants may look back at most 6 months. The first day of that earliest
  // month is the floor for both month navigation and day selection.
  const earliestPast = useMemo(() => earliestMerchantPastDate(), []);
  const earliestMonthStart = useMemo(
    () => new Date(earliestPast.getFullYear(), earliestPast.getMonth(), 1),
    [earliestPast]
  );
  const canGoPrevMonth = viewMonth.getTime() > earliestMonthStart.getTime();

  const selectedKey = useMemo(() => {
    if (!selectedDate) return '';
    return `${selectedDate.getFullYear()}-${selectedDate.getMonth()}-${selectedDate.getDate()}`;
  }, [selectedDate]);

  const calendarDays = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const days = [];
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = daysInPrevMonth - i;
      days.push({ day: d, month: month - 1, year, otherMonth: true });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ day: d, month, year });
    }
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      days.push({ day: d, month: month + 1, year, otherMonth: true });
    }
    return days;
  }, [viewMonth]);

  return (
    <div className="appointments-v2-mini-cal">
      <div className="appointments-v2-mini-cal-nav">
        <button
          type="button"
          className="appointments-v2-mini-cal-arrow"
          onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
          disabled={!canGoPrevMonth}
          aria-disabled={!canGoPrevMonth}
          title={!canGoPrevMonth ? 'History is limited to the last 6 months' : undefined}
        >
          <ChevronLeft size={14} />
        </button>
        <span className="appointments-v2-mini-cal-month">{MONTH_NAMES[viewMonth.getMonth()]} {viewMonth.getFullYear()}</span>
        <button type="button" className="appointments-v2-mini-cal-arrow" onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}>
          <ChevronRight size={14} />
        </button>
      </div>
      <div className="appointments-v2-mini-cal-grid">
        {DAYS_OF_WEEK.map((d) => <span key={d} className="appointments-v2-mini-cal-dow">{d}</span>)}
        {calendarDays.map((cell, i) => {
          const key = `${cell.year}-${cell.month}-${cell.day}`;
          const cellDate = new Date(cell.year, cell.month, cell.day);
          const isBeforeWindow = cellDate.getTime() < earliestPast.getTime();
          const isToday = key === today;
          const isSelected = key === selectedKey;
          const hasAppt = bookingDates.has(key);
          const cls = [
            'appointments-v2-mini-cal-day',
            cell.otherMonth ? 'other-month' : '',
            isToday ? 'today' : '',
            isSelected ? 'selected' : '',
            hasAppt ? 'has-appointments' : '',
            isBeforeWindow ? 'out-of-window' : ''
          ].filter(Boolean).join(' ');
          return (
            <button
              key={`cal-${i}`}
              type="button"
              className={cls}
              onClick={() => onDateSelect(new Date(cell.year, cell.month, cell.day))}
              disabled={isBeforeWindow}
              aria-disabled={isBeforeWindow}
              title={isBeforeWindow ? 'History is limited to the last 6 months' : undefined}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const StoreAppointments = ({
  request,
  onToast,
  initialTab = 'today',
  showWorkspaceTabs = true,
  headerTitle = 'Appointments',
  onReviewDrafts = null
}) => {
  const storeToken = useSelector((state) => state.store.storeToken);
  const [innerTab, setInnerTab] = useState(initialTab);
  // CP-97 — merchant ROI headline ("Zopeo brought you ₹X this month").
  const [roiSummary, setRoiSummary] = useState(null);

  useEffect(() => {
    if (!request) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await request(ANALYTICS_ROI_SUMMARY, { method: 'GET', requestName: 'roi-summary' });
        if (!cancelled) setRoiSummary(response?.data || null);
      } catch (error) {
        // The ROI headline is additive — never block the appointments page over it.
        if (!cancelled) setRoiSummary(null);
      }
    })();
    return () => { cancelled = true; };
  }, [request]);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [calendarView, setCalendarView] = useState('day');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsDraft, setSettingsDraft] = useState(DEFAULT_SETTINGS);
  const [isEditingGlobalSettings, setIsEditingGlobalSettings] = useState(false);
  const isEditingGlobalSettingsRef = useRef(false);
  useEffect(() => {
    isEditingGlobalSettingsRef.current = isEditingGlobalSettings;
  }, [isEditingGlobalSettings]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [slotsByService, setSlotsByService] = useState({});
  const [serviceSlotSummariesByService, setServiceSlotSummariesByService] = useState({});
  const [selectedServiceSlot, setSelectedServiceSlot] = useState(null);
  const [serviceFilter, setServiceFilter] = useState('all');
  const [yesterdayNoShows, setYesterdayNoShows] = useState(0);
  const [showServiceEditor, setShowServiceEditor] = useState(false);
  const [serviceForm, setServiceForm] = useState(EMPTY_SERVICE_FORM);
  const [editingServiceUuid, setEditingServiceUuid] = useState('');
  const [deleteServiceTarget, setDeleteServiceTarget] = useState(null);
  const [isDeletingService, setIsDeletingService] = useState(false);
  const [serviceDrafts, setServiceDrafts] = useState({});
  const [serviceHoldEditors, setServiceHoldEditors] = useState({});
  const [persistedServiceHolds, setPersistedServiceHolds] = useState({});
  const [serviceEditBaselines, setServiceEditBaselines] = useState({});
  const [editingServiceCardId, setEditingServiceCardId] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isSavingService, setIsSavingService] = useState(false);
  const [walkinModalOpen, setWalkinModalOpen] = useState(false);
  const [isCreatingWalkin, setIsCreatingWalkin] = useState(false);
  const [deleteSlotModalOpen, setDeleteSlotModalOpen] = useState(false); // F42: Delete slot modal state
  const [isDeletingSlot, setIsDeletingSlot] = useState(false); // F42: Delete slot loading state
  const [slotToDelete, setSlotToDelete] = useState(null); // F42: Slot to delete data
  const [editWalkinModalOpen, setEditWalkinModalOpen] = useState(false); // F45: Edit walkin modal state
  const [editingBooking, setEditingBooking] = useState(null); // F45: Booking being edited
  const [deleteWalkinTarget, setDeleteWalkinTarget] = useState(null); // F45: Walkin booking to delete
  const [isDeletingWalkin, setIsDeletingWalkin] = useState(false); // F45: Delete walkin loading state
  const [arrivalOtpDialogBooking, setArrivalOtpDialogBooking] = useState(null);
  const [arrivalOtpValue, setArrivalOtpValue] = useState('');
  const [isVerifyingArrivalOtp, setIsVerifyingArrivalOtp] = useState(false);
  const [selectedAppointmentSummary, setSelectedAppointmentSummary] = useState(null);
  const [selectedAppointmentDetails, setSelectedAppointmentDetails] = useState(null);
  const [isAppointmentDetailsOpen, setIsAppointmentDetailsOpen] = useState(false);
  const [isLoadingAppointmentDetails, setIsLoadingAppointmentDetails] = useState(false);
  const [bookingSearchQuery, setBookingSearchQuery] = useState('');
  // Appointment-history download (date-range CSV export).
  // Defaults to the last 1 month; the merchant can widen the start back to the
  // 6-month floor (historyMinDate).
  const historyMinDate = useMemo(() => earliestMerchantPastDateIso(), []);
  const historyDefaultStart = useMemo(() => monthsAgoIso(DEFAULT_HISTORY_MONTHS), []);
  const todayIso = useMemo(() => formatDateInput(new Date()), []);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyStartDate, setHistoryStartDate] = useState(historyDefaultStart);
  const [historyEndDate, setHistoryEndDate] = useState(todayIso);
  const [isDownloadingHistory, setIsDownloadingHistory] = useState(false);
  const slotDetailPanelRef = useRef(null); // F41: Auto-scroll to slot detail
  const bookingTimelineRefs = useRef(new Map());
  const timelineScrollContainerRef = useRef(null);
  const [activeRequestCount, setActiveRequestCount] = useState(0);
  const [bootstrapError, setBootstrapError] = useState('');
  const [hasSettingsLoaded, setHasSettingsLoaded] = useState(false);
  const [hasBookingsLoaded, setHasBookingsLoaded] = useState(false);
  const [timelineFilter] = useState('all');
  const [appointmentStatusFilter] = useState('all');
  const [customTimelineStart] = useState('09:00');
  const [customTimelineEnd] = useState('18:00');
  const [bookingSort] = useState({ key: 'time', direction: 'asc' });

  const isGlobalLoading = activeRequestCount > 0;
  const timezone = String(settings.timezone || '').trim() || detectBrowserTimezone();
  const selectedDateKey = useMemo(() => formatDateInput(selectedDate), [selectedDate]);
  const serviceMap = useMemo(() => {
    return new Map(services.map((service) => [service.serviceUuid, service]));
  }, [services]);

  useEffect(() => {
    setInnerTab(initialTab);
  }, [initialTab]);

  const {
    fetchSettings: fetchAppointmentSettings,
    fetchServices: fetchAppointmentServices,
    fetchBookings: fetchAppointmentBookings,
    fetchBookingsRange: fetchAppointmentBookingsRange,
    fetchBookingDetails: fetchAppointmentBookingDetails,
    fetchSlots: fetchAppointmentSlots,
    fetchServiceSlotSummaries: fetchAppointmentServiceSlotSummaries,
    saveSettings: saveAppointmentSettings,
    updateService: updateAppointmentService,
    createService: createAppointmentService,
    deleteService: deleteAppointmentService,
    createBooking: createAppointmentBooking,
    fetchAllHolds: fetchAppointmentHolds,
    createHold: createAppointmentHold,
    createDayHold: createAppointmentDayHold,
    deleteHold: deleteAppointmentHold,
    deleteSlot: deleteSlotRequest, // F42: Delete slot API
    editWalkinBooking, // F45: Edit walkin booking API
    deleteWalkinBooking, // F45: Delete walkin booking API
    updateBookingStatus, // F46: Update booking status API
    issueArrivalOtp,
    verifyArrivalOtp
  } = useAppointments({ request, storeToken, onToast });

  const runWithGlobalLoader = useCallback(async (executor) => {
    setActiveRequestCount((previous) => previous + 1);
    try {
      return await executor();
    } finally {
      setActiveRequestCount((previous) => Math.max(0, previous - 1));
    }
  }, []);

  const pushRangeValidationError = useCallback((message) => {
    onToast?.({ message, variant: 'error' });
  }, [onToast]);

  const openHistoryModal = useCallback(() => {
    setHistoryStartDate(historyDefaultStart);
    setHistoryEndDate(todayIso);
    setIsHistoryModalOpen(true);
  }, [historyDefaultStart, todayIso]);

  const handleDownloadHistory = useCallback(async () => {
    if (!historyStartDate || !historyEndDate) {
      onToast?.({ message: 'Pick a start and end date.', variant: 'error' });
      return;
    }
    if (historyStartDate > historyEndDate) {
      onToast?.({ message: 'Start date cannot be after end date.', variant: 'error' });
      return;
    }
    setIsDownloadingHistory(true);
    try {
      const response = await fetchAppointmentBookingsRange(historyStartDate, historyEndDate);
      const bookings = Array.isArray(response?.data) ? response.data : [];
      if (bookings.length === 0) {
        onToast?.({ message: 'No appointments found in this date range.', variant: 'info' });
        return;
      }
      const rows = bookingsToCsvRows(bookings);
      downloadCsv(bookingHistoryFilename(historyStartDate, historyEndDate), rows, {
        title: 'Appointment history',
        meta: [`Date range: ${historyStartDate} to ${historyEndDate}`]
      });
      onToast?.({ message: `Downloaded ${bookings.length} appointment${bookings.length === 1 ? '' : 's'}.`, variant: 'success' });
      setIsHistoryModalOpen(false);
    } catch (error) {
      // ensureRequest already surfaces a toast; keep the modal open to retry.
    } finally {
      setIsDownloadingHistory(false);
    }
  }, [fetchAppointmentBookingsRange, historyStartDate, historyEndDate, onToast]);

  const handleSettingsTimeChange = useCallback((field, nextValue) => {
    if (!isEditingGlobalSettings) {
      return;
    }
    if (!['weekdayOpen', 'weekdayClose', 'weekendOpen', 'weekendClose'].includes(field)) {
      return;
    }
    const nextWeekdayOpen = field === 'weekdayOpen' ? nextValue : settingsDraft.weekdayOpen;
    const nextWeekdayClose = field === 'weekdayClose' ? nextValue : settingsDraft.weekdayClose;
    const nextWeekendOpen = field === 'weekendOpen' ? nextValue : settingsDraft.weekendOpen;
    const nextWeekendClose = field === 'weekendClose' ? nextValue : settingsDraft.weekendClose;

    if (isFromAfterTo(nextWeekdayOpen, nextWeekdayClose)) {
      pushRangeValidationError('Weekday opening time cannot be later than closing time.');
      return;
    }
    if (isFromAfterTo(nextWeekendOpen, nextWeekendClose)) {
      pushRangeValidationError('Weekend opening time cannot be later than closing time.');
      return;
    }

    setSettingsDraft((previous) => ({ ...previous, [field]: nextValue }));
  }, [
    isEditingGlobalSettings,
    pushRangeValidationError,
    settingsDraft.weekdayClose,
    settingsDraft.weekdayOpen,
    settingsDraft.weekendClose,
    settingsDraft.weekendOpen
  ]);

  const handleServiceTimeDraftChange = useCallback((service, field, nextValue) => {
    if (!service?.serviceUuid || !['openTime', 'closeTime'].includes(field)) {
      return;
    }
    if (editingServiceCardId !== service.serviceUuid) {
      return;
    }
    const currentDraft = serviceDrafts[service.serviceUuid] || {};
    const currentOpen = currentDraft.openTime ?? settings.weekdayOpen ?? '09:00';
    const currentClose = currentDraft.closeTime ?? settings.weekdayClose ?? '21:00';
    const nextOpen = field === 'openTime' ? nextValue : currentOpen;
    const nextClose = field === 'closeTime' ? nextValue : currentClose;

    if (isFromAfterTo(nextOpen, nextClose)) {
      pushRangeValidationError(`${service.serviceName || 'Service'} opening time cannot be later than closing time.`);
      return;
    }

    setServiceDrafts((previous) => ({
      ...previous,
      [service.serviceUuid]: { ...(previous[service.serviceUuid] || {}), [field]: nextValue }
    }));
  }, [editingServiceCardId, pushRangeValidationError, serviceDrafts, settings.weekdayClose, settings.weekdayOpen]);

  const fetchSettingsAndServices = useCallback(async () => {
    if (!request || !storeToken) {
      return;
    }
    try {
      const [settingsResponse, servicesResponse, holdsResponse] = await runWithGlobalLoader(() => Promise.all([
        fetchAppointmentSettings(),
        fetchAppointmentServices(),
        fetchAppointmentHolds()
      ]));
      const nextSettings = settingsResponse?.data || {};
      const nextServices = Array.isArray(servicesResponse?.data) ? servicesResponse.data : [];
      const nextHolds = Array.isArray(holdsResponse?.data) ? holdsResponse.data : [];
      const holdsByService = nextHolds.reduce((acc, hold) => {
        const serviceUuid = hold?.serviceUuid;
        if (!serviceUuid) {
          return acc;
        }
        acc[serviceUuid] = [...(acc[serviceUuid] || []), normalizeServiceScheduleHold(hold)];
        return acc;
      }, {});
      setPersistedServiceHolds(holdsByService);
      setSettings((previous) => ({ ...previous, ...nextSettings }));
      setSettingsDraft((previous) => (
        isEditingGlobalSettingsRef.current
          ? previous
          : { ...previous, ...nextSettings }
      ));
      setServices(nextServices.map((service) => ({
        ...service,
        scheduleHolds: normalizeServiceScheduleHolds(
          holdsByService[service.serviceUuid] ?? service.scheduleHolds
        )
      })));
      setBootstrapError('');
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Unable to load appointment settings.';
      onToast?.({ message, variant: 'error' });
      setBootstrapError(message);
    } finally {
      setHasSettingsLoaded(true);
    }
  }, [
    fetchAppointmentServices,
    fetchAppointmentSettings,
    fetchAppointmentHolds,
    onToast,
    request,
    runWithGlobalLoader,
    storeToken
  ]);

  const fetchBookings = useCallback(async () => {
    if (!request || !storeToken) {
      return;
    }
    const { windowKey, dateString } = resolveBookingsRequest(selectedDate);
    try {
      const response = await runWithGlobalLoader(() => fetchAppointmentBookings(windowKey, dateString));
      const incoming = Array.isArray(response?.data) ? response.data : [];
      const sorted = incoming
        .sort((first, second) => {
          const firstDate = parseDateValue(first.appointmentStartTime)?.getTime() || 0;
          const secondDate = parseDateValue(second.appointmentStartTime)?.getTime() || 0;
          return firstDate - secondDate;
        });
      setBookings(sorted);

      if (windowKey === 'today') {
        const previousResponse = await runWithGlobalLoader(() => fetchAppointmentBookings('previous'));
        const previousList = Array.isArray(previousResponse?.data) ? previousResponse.data : [];
        const yesterdayKey = formatDateInput(addDays(new Date(), -1));
        const yesterdayCount = previousList.filter((booking) => (
          extractDateKey(booking.appointmentStartTime) === yesterdayKey
          && normalizeBookingStatus(booking.status) === 'missed'
        )).length;
        setYesterdayNoShows(yesterdayCount);
      } else {
        setYesterdayNoShows(0);
      }
      setBootstrapError('');
    } catch (error) {
      setBookings([]);
      const message = error?.data?.message || error?.message || 'Unable to load bookings.';
      onToast?.({ message, variant: 'error' });
      setBootstrapError((previous) => previous || message);
    } finally {
      setHasBookingsLoaded(true);
    }
  }, [
    fetchAppointmentBookings,
    onToast,
    request,
    runWithGlobalLoader,
    selectedDate,
    storeToken
  ]);

  const fetchSlots = useCallback(async () => {
    if (!request || !storeToken || !services.length) {
      setSlotsByService({});
      return;
    }
    const serviceCatalog = services.filter((service) => service.active !== false);
    if (!serviceCatalog.length) {
      setSlotsByService({});
      return;
    }
    try {
      const slotEntries = await runWithGlobalLoader(() => Promise.all(
        serviceCatalog.map(async (service) => {
          try {
            const response = await fetchAppointmentSlots(service.serviceUuid, selectedDateKey);
            return [service.serviceUuid, Array.isArray(response?.data) ? response.data : []];
          } catch (_error) {
            return [service.serviceUuid, []];
          }
        })
      ));

      const nextMap = {};
      slotEntries.forEach(([serviceId, slots]) => {
        nextMap[serviceId] = slots;
      });
      setSlotsByService(nextMap);
    } catch (error) {
      setSlotsByService({});
      const message = error?.data?.message || error?.message || 'Unable to load slots.';
      onToast?.({ message, variant: 'error' });
    }
  }, [
    fetchAppointmentSlots,
    onToast,
    request,
    runWithGlobalLoader,
    selectedDateKey,
    services,
    storeToken
  ]);

  const fetchServiceSlotSummaries = useCallback(async () => {
    if (!request || !storeToken || !services.length) {
      setServiceSlotSummariesByService({});
      return;
    }
    try {
      const response = await runWithGlobalLoader(() => fetchAppointmentServiceSlotSummaries(selectedDateKey));
      const incoming = Array.isArray(response?.data) ? response.data : [];
      const nextMap = {};
      incoming.forEach((summary) => {
        if (summary?.serviceUuid) {
          nextMap[summary.serviceUuid] = summary;
        }
      });
      setServiceSlotSummariesByService(nextMap);
    } catch (error) {
      setServiceSlotSummariesByService({});
      const message = error?.data?.message || error?.message || 'Unable to load service slot summaries.';
      onToast?.({ message, variant: 'error' });
    }
  }, [
    fetchAppointmentServiceSlotSummaries,
    onToast,
    request,
    runWithGlobalLoader,
    selectedDateKey,
    services.length,
    storeToken
  ]);

  const handleCreateWalkin = useCallback(async (payload) => {
    setIsCreatingWalkin(true);
    try {
      const response = await createAppointmentBooking(payload);
      if (response?.success) {
        onToast?.({ message: 'Walk-in added successfully', variant: 'success' });
        setWalkinModalOpen(false);
        await fetchServiceSlotSummaries();
        await fetchBookings();
      }
    } catch (error) {
      onToast?.({ message: error?.message || 'Failed to create walk-in', variant: 'error' });
    } finally {
      setIsCreatingWalkin(false);
    }
  }, [createAppointmentBooking, fetchBookings, fetchServiceSlotSummaries, onToast]);

  // F42: Handle slot deletion with cascading cancellation
  const handleDeleteSlot = useCallback(async (cancellationReason) => {
    if (!slotToDelete) return;

    setIsDeletingSlot(true);
    try {
      const response = await deleteSlotRequest(
        slotToDelete.serviceUuid,
        slotToDelete.date,
        slotToDelete.startTime,
        slotToDelete.endTime,
        cancellationReason
      );

      if (response?.success) {
        const bookingCount = response.data?.bookingsCancelled || 0;
        const message = bookingCount > 0
          ? `Slot deleted. ${bookingCount} customer${bookingCount === 1 ? '' : 's'} notified.`
          : 'Slot deleted and blocked for future bookings.';

        onToast?.({ message, variant: 'success' });
        setDeleteSlotModalOpen(false);
        setSlotToDelete(null);
        setSelectedServiceSlot(null);

        // Refresh schedule using local wrapper functions
        await fetchServiceSlotSummaries();
        await fetchBookings();
      }
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Failed to delete slot';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsDeletingSlot(false);
    }
  }, [slotToDelete, deleteSlotRequest, onToast, fetchServiceSlotSummaries, fetchBookings]);

  // "Done" is a point of no return: services/offers lock and the visit summary
  // is generated — so it gets an explicit confirmation modal first.
  const [confirmDoneBooking, setConfirmDoneBooking] = useState(null);
  const [isMarkingDone, setIsMarkingDone] = useState(false);

  // F46: Handle booking status change from timeline card
  const handleStatusChange = useCallback(async (booking, newStatus) => {
    const currentStatus = normalizeBookingStatus(booking?.status);
    if (!booking?.bookingUuid || currentStatus === newStatus) {
      return;
    }
    if (isPastDateRef.current) {
      onToast?.({ message: 'Status cannot be updated for past dates.', variant: 'error' });
      return;
    }
    if (newStatus === 'done') {
      setConfirmDoneBooking(booking);
      return;
    }

    try {
      // Moving a confirmed booking to In Progress is gated by the arrival OTP —
      // verifying the OTP transitions it straight to IN_PROGRESS (no Arrived step).
      if (currentStatus === 'confirmed' && newStatus === 'in_progress') {
        const otpIssueResponse = await issueArrivalOtp(booking.bookingUuid);
        if (otpIssueResponse?.success) {
          onToast?.({ message: otpIssueResponse.message || 'Arrival OTP sent to customer.', variant: 'success' });
          setArrivalOtpDialogBooking(booking);
          setArrivalOtpValue('');
        }
        return;
      }

      const response = await updateBookingStatus(booking.bookingUuid, newStatus);
      if (response?.success) {
        onToast?.({ message: 'Status updated', variant: 'success' });
        await fetchServiceSlotSummaries();
        await fetchBookings();
      }
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Unable to update status.';
      onToast?.({ message, variant: 'error' });
    }
  }, [updateBookingStatus, issueArrivalOtp, onToast, fetchServiceSlotSummaries, fetchBookings]);

  const confirmMarkDone = useCallback(async () => {
    const booking = confirmDoneBooking;
    if (!booking?.bookingUuid) {
      setConfirmDoneBooking(null);
      return;
    }
    setIsMarkingDone(true);
    try {
      const response = await updateBookingStatus(booking.bookingUuid, 'done');
      if (response?.success) {
        onToast?.({ message: 'Visit completed — final summary generated', variant: 'success' });
        await fetchServiceSlotSummaries();
        await fetchBookings();
      }
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Unable to update status.';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsMarkingDone(false);
      setConfirmDoneBooking(null);
    }
  }, [confirmDoneBooking, updateBookingStatus, onToast, fetchServiceSlotSummaries, fetchBookings]);

  const closeArrivalOtpDialog = useCallback(() => {
    if (isVerifyingArrivalOtp) {
      return;
    }
    setArrivalOtpDialogBooking(null);
    setArrivalOtpValue('');
  }, [isVerifyingArrivalOtp]);

  const confirmArrivalOtp = useCallback(async () => {
    if (!arrivalOtpDialogBooking?.bookingUuid || !arrivalOtpValue.trim()) {
      return;
    }

    setIsVerifyingArrivalOtp(true);
    try {
      const response = await verifyArrivalOtp(arrivalOtpDialogBooking.bookingUuid, arrivalOtpValue.trim());
      if (response?.success) {
        onToast?.({ message: 'Arrival verified', variant: 'success' });
        setArrivalOtpDialogBooking(null);
        setArrivalOtpValue('');
        await fetchServiceSlotSummaries();
        await fetchBookings();
      }
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Unable to verify arrival OTP.';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsVerifyingArrivalOtp(false);
    }
  }, [arrivalOtpDialogBooking, arrivalOtpValue, verifyArrivalOtp, onToast, fetchServiceSlotSummaries, fetchBookings]);

  // F45: Handle edit walkin booking click
  const handleEditWalkinClick = useCallback((booking) => {
    setEditingBooking(booking);
    setEditWalkinModalOpen(true);
  }, []);

  // F45: Handle edit walkin booking submit
  const handleEditWalkinSubmit = useCallback(async (payload) => {
    if (!editingBooking) return;

    try {
      const response = await editWalkinBooking(editingBooking.bookingUuid, payload);
      if (response?.success) {
        onToast?.({ message: 'Walk-in booking updated successfully', variant: 'success' });
        setEditWalkinModalOpen(false);
        setEditingBooking(null);
        await fetchServiceSlotSummaries();
        await fetchBookings();
      }
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Failed to update walk-in booking';
      onToast?.({ message, variant: 'error' });
      throw error;
    }
  }, [editingBooking, editWalkinBooking, fetchBookings, fetchServiceSlotSummaries, onToast]);

  // F45: Handle delete walkin booking confirmation
  const confirmDeleteWalkin = useCallback(async () => {
    if (!deleteWalkinTarget) return;

    setIsDeletingWalkin(true);
    try {
      const response = await deleteWalkinBooking(deleteWalkinTarget.bookingUuid);
      if (response?.success) {
        onToast?.({ message: 'Walk-in booking deleted successfully', variant: 'success' });
        setDeleteWalkinTarget(null);
        await fetchServiceSlotSummaries();
        await fetchBookings();
      }
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Failed to delete walk-in booking';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsDeletingWalkin(false);
    }
  }, [deleteWalkinTarget, deleteWalkinBooking, fetchBookings, fetchServiceSlotSummaries, onToast]);

  useEffect(() => {
    fetchSettingsAndServices();
  }, [fetchSettingsAndServices]);

  // F41: Auto-scroll to slot detail panel when slot is selected
  useEffect(() => {
    if (selectedServiceSlot && slotDetailPanelRef.current) {
      setTimeout(() => {
        slotDetailPanelRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [selectedServiceSlot]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  useEffect(() => {
    fetchServiceSlotSummaries();
  }, [fetchServiceSlotSummaries]);

  useEffect(() => {
    setServiceSlotSummariesByService((previous) => {
      const serviceIds = new Set(services.map((service) => service.serviceUuid));
      const next = {};
      Object.entries(previous).forEach(([serviceUuid, summary]) => {
        if (serviceIds.has(serviceUuid)) {
          next[serviceUuid] = summary;
        }
      });
      return Object.keys(next).length === Object.keys(previous).length ? previous : next;
    });
  }, [services]);

  useEffect(() => {
    if (String(settings.timezone || '').trim()) {
      return;
    }
    const timezoneValue = detectBrowserTimezone();
    setSettings((previous) => ({ ...previous, timezone: timezoneValue }));
    setSettingsDraft((previous) => ({ ...previous, timezone: timezoneValue }));
  }, [settings.timezone]);

  useEffect(() => {
    if (serviceFilter === 'all') {
      return;
    }
    if (!services.some((service) => service.serviceUuid === serviceFilter)) {
      setServiceFilter('all');
    }
  }, [serviceFilter, services]);

  useEffect(() => {
    if (!editingServiceCardId) {
      return;
    }
    if (!services.some((service) => service.serviceUuid === editingServiceCardId)) {
      setEditingServiceCardId('');
    }
  }, [editingServiceCardId, services]);

  useEffect(() => {
    setServiceDrafts((previous) => {
      const next = { ...previous };
      services.forEach((service) => {
        const draft = previous[service.serviceUuid] || {};
        const fallbackScheduleHolds = persistedServiceHolds[service.serviceUuid] ?? service.scheduleHolds;
        next[service.serviceUuid] = {
          durationMinutes: draft.durationMinutes ?? (Number(service.appointmentDurationMins) || 30),
          capacityPerSlot: draft.capacityPerSlot ?? (Number(service.maxBookingsPerSlot) || 1),
          price: draft.price ?? (service.depositAmount ?? ''),
          openTime: draft.openTime ?? settings.weekdayOpen ?? '09:00',
          closeTime: draft.closeTime ?? settings.weekdayClose ?? '21:00',
          extraField: draft.extraField ?? '',
          active: draft.active ?? (service.active !== false),
          activeDays: Array.isArray(draft.activeDays)
            ? draft.activeDays
            : (Array.isArray(service.activeDays) ? service.activeDays : WEEKDAY_CHIPS.map((chip) => chip.index)),
          scheduleHolds: Array.isArray(draft.scheduleHolds)
            ? normalizeServiceScheduleHolds(draft.scheduleHolds)
            : normalizeServiceScheduleHolds(fallbackScheduleHolds)
        };
      });
      return next;
    });
  }, [persistedServiceHolds, services, settings.weekdayClose, settings.weekdayOpen]);

  useEffect(() => {
    if (showServiceEditor || isAppointmentDetailsOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [isAppointmentDetailsOpen, showServiceEditor]);

  const closeAppointmentDetails = useCallback(() => {
    setIsAppointmentDetailsOpen(false);
    setIsLoadingAppointmentDetails(false);
    setSelectedAppointmentDetails(null);
    setSelectedAppointmentSummary(null);
  }, []);

  // W3 (appointments tab) — attach/detach an offer on a walk-in booking
  // ("I saw this offer in the app" at the counter). Uses the W2 endpoints.
  const [offerPickerBooking, setOfferPickerBooking] = useState(null);
  const [walkinPickerOffers, setWalkinPickerOffers] = useState([]);
  // Membership benefit candidate for the picker (null when not a member).
  const [walkinPickerMembership, setWalkinPickerMembership] = useState(null);
  const [walkinPickerLoyaltyReward, setWalkinPickerLoyaltyReward] = useState(null);
  const [walkinPickerLoading, setWalkinPickerLoading] = useState(false);
  const [walkinPickerSearch, setWalkinPickerSearch] = useState('');
  const [isWalkinOfferMutating, setIsWalkinOfferMutating] = useState(false);

  const openWalkinOfferPicker = useCallback(async (booking) => {
    if (!booking?.bookingUuid) {
      return;
    }
    setOfferPickerBooking(booking);
    setWalkinPickerSearch('');
    setWalkinPickerOffers([]);
    setWalkinPickerLoading(true);
    try {
      // W3+ — per-customer candidates: every live offer comes back with an
      // eligibility verdict for THIS walk-in (audience, caps, bucket, cart math).
      const response = await request(
        `${PROFILE_STORES_OFFERS}/walkins/bookings/${booking.bookingUuid}/offers`,
        {
          method: 'GET',
          requestName: 'walkin-offer-picker-list'
        }
      );
      // request() returns the parsed ApiResponse envelope; the payload is now
      // { membership, offers } — the member's benefit pinned above the offers.
      const payload = response?.data;
      const list = Array.isArray(payload) ? payload : Array.isArray(payload?.offers) ? payload.offers : [];
      setWalkinPickerMembership(payload?.membership || null);
      setWalkinPickerLoyaltyReward(payload?.loyaltyReward || null);
      setWalkinPickerOffers([...list].sort(
        (a, b) => Number(b?.eligible === true) - Number(a?.eligible === true)
      ));
    } catch (error) {
      onToast?.({ message: 'Could not load offers. Try again.', variant: 'error' });
      setOfferPickerBooking(null);
    } finally {
      setWalkinPickerLoading(false);
    }
  }, [request, onToast]);

  const attachOfferToWalkinBooking = useCallback(async (offer) => {
    if (!offerPickerBooking?.bookingUuid || !offer?.id) {
      return;
    }
    setIsWalkinOfferMutating(true);
    try {
      await request(`${PROFILE_STORES_OFFERS}/walkins/bookings/${offerPickerBooking.bookingUuid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        requestName: 'walkin-booking-offer-attach',
        body: JSON.stringify({ offerId: offer.id })
      });
      onToast?.({ message: `Offer attached to ${offerPickerBooking.customerName || 'walk-in'}`, variant: 'success' });
      setOfferPickerBooking(null);
      await fetchBookings();
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Could not attach the offer.';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsWalkinOfferMutating(false);
    }
  }, [offerPickerBooking, request, onToast, fetchBookings]);

  const attachMembershipToWalkinBooking = useCallback(async () => {
    if (!offerPickerBooking?.bookingUuid) {
      return;
    }
    setIsWalkinOfferMutating(true);
    try {
      await request(`${PROFILE_STORES_OFFERS}/walkins/bookings/${offerPickerBooking.bookingUuid}/membership`, {
        method: 'POST',
        requestName: 'walkin-membership-attach'
      });
      onToast?.({ message: `Membership benefit attached to ${offerPickerBooking.customerName || 'walk-in'}`, variant: 'success' });
      setOfferPickerBooking(null);
      await fetchBookings();
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Could not attach the membership benefit.';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsWalkinOfferMutating(false);
    }
  }, [offerPickerBooking, request, onToast, fetchBookings]);

  // CP-111 (A2): explicit pick IS the swap — attaching the reward displaces
  // whatever benefit holds the slot (offer claim released, membership steps aside).
  const attachLoyaltyRewardToWalkinBooking = useCallback(async () => {
    if (!offerPickerBooking?.bookingUuid) {
      return;
    }
    setIsWalkinOfferMutating(true);
    try {
      await request(`${PROFILE_STORES_OFFERS}/walkins/bookings/${offerPickerBooking.bookingUuid}/loyalty-reward`, {
        method: 'POST',
        requestName: 'walkin-loyalty-reward-attach'
      });
      onToast?.({ message: `Loyalty reward attached to ${offerPickerBooking.customerName || 'walk-in'}`, variant: 'success' });
      setOfferPickerBooking(null);
      await fetchBookings();
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Could not attach the loyalty reward.';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsWalkinOfferMutating(false);
    }
  }, [offerPickerBooking, request, onToast, fetchBookings]);

  const detachLoyaltyRewardFromWalkinBooking = useCallback(async (booking) => {
    if (!booking?.bookingUuid) {
      return;
    }
    setIsWalkinOfferMutating(true);
    try {
      await request(`${PROFILE_STORES_OFFERS}/walkins/bookings/${booking.bookingUuid}/loyalty-reward`, {
        method: 'DELETE',
        requestName: 'walkin-loyalty-reward-detach'
      });
      onToast?.({ message: 'Loyalty reward removed — it stays in the customer\'s wallet', variant: 'success' });
      await fetchBookings();
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Could not remove the loyalty reward.';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsWalkinOfferMutating(false);
    }
  }, [request, onToast, fetchBookings]);

  const detachMembershipFromWalkinBooking = useCallback(async (booking) => {
    if (!booking?.bookingUuid) {
      return;
    }
    setIsWalkinOfferMutating(true);
    try {
      await request(`${PROFILE_STORES_OFFERS}/walkins/bookings/${booking.bookingUuid}/membership`, {
        method: 'DELETE',
        requestName: 'walkin-membership-detach'
      });
      onToast?.({ message: 'Membership benefit removed', variant: 'success' });
      await fetchBookings();
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Could not remove the membership benefit.';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsWalkinOfferMutating(false);
    }
  }, [request, onToast, fetchBookings]);

  const detachOfferFromWalkinBooking = useCallback(async (booking) => {
    if (!booking?.bookingUuid) {
      return;
    }
    setIsWalkinOfferMutating(true);
    try {
      await request(`${PROFILE_STORES_OFFERS}/walkins/bookings/${booking.bookingUuid}`, {
        method: 'DELETE',
        requestName: 'walkin-booking-offer-detach'
      });
      onToast?.({ message: 'Offer removed — claim released back to the customer', variant: 'success' });
      await fetchBookings();
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Could not remove the offer.';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsWalkinOfferMutating(false);
    }
  }, [request, onToast, fetchBookings]);

  const visibleWalkinPickerOffers = useMemo(() => {
    const query = walkinPickerSearch.trim().toLowerCase();
    if (!query) {
      return walkinPickerOffers;
    }
    return walkinPickerOffers.filter((offer) => (
      String(offer?.title || '').toLowerCase().includes(query)
      || String(offer?.couponCode || '').toLowerCase().includes(query)
    ));
  }, [walkinPickerOffers, walkinPickerSearch]);

  const openAppointmentDetails = useCallback(async (booking) => {
    if (!booking?.bookingUuid) {
      return;
    }
    setSelectedAppointmentSummary({
      bookingUuid: booking.bookingUuid,
      customerName: booking.customerName,
      customerPhone: booking.customerPhone
    });
    setSelectedAppointmentDetails(null);
    setIsAppointmentDetailsOpen(true);
    setIsLoadingAppointmentDetails(true);
    try {
      const response = await runWithGlobalLoader(() => fetchAppointmentBookingDetails(booking.bookingUuid));
      setSelectedAppointmentDetails(response?.data || null);
    } finally {
      setIsLoadingAppointmentDetails(false);
    }
  }, [fetchAppointmentBookingDetails, runWithGlobalLoader]);

  const selectedReminderOffsets = useMemo(
    () => parseReminderOffsets(settingsDraft.reminderOffsets),
    [settingsDraft.reminderOffsets]
  );

  const hasInvalidGlobalTiming = useMemo(
    () => (
      isFromAfterTo(settingsDraft.weekdayOpen, settingsDraft.weekdayClose)
      || isFromAfterTo(settingsDraft.weekendOpen, settingsDraft.weekendClose)
    ),
    [
      settingsDraft.weekdayClose,
      settingsDraft.weekdayOpen,
      settingsDraft.weekendClose,
      settingsDraft.weekendOpen
    ]
  );

  const cancellationWindowValidation = useMemo(() => {
    const value = settingsDraft.cancellationWindowHours;
    // Allow null/undefined during editing (will default to 4 on blur)
    if (value == null || value === '') return { valid: true, error: null, warning: null };
    if (value < 0) return { valid: false, error: 'Cannot be negative', warning: null };
    if (value > 168) return { valid: false, error: 'Cannot exceed 168 hours (7 days)', warning: null };
    if (value === 0) return { valid: true, error: null, warning: 'Setting to 0 allows customers to cancel anytime, including last-minute cancellations and missed appointments' };
    return { valid: true, error: null, warning: null };
  }, [settingsDraft.cancellationWindowHours]);

  const rescheduleWindowValidation = useMemo(() => {
    const value = settingsDraft.rescheduleWindowHours;
    if (value == null || value === '') return { valid: true, error: null, warning: null };
    if (value < 0) return { valid: false, error: 'Cannot be negative', warning: null };
    if (value > 168) return { valid: false, error: 'Cannot exceed 168 hours (7 days)', warning: null };
    if (value === 0) return { valid: true, error: null, warning: 'Setting to 0 allows customers to reschedule anytime before their appointment.' };
    return { valid: true, error: null, warning: null };
  }, [settingsDraft.rescheduleWindowHours]);

  const visibleServices = useMemo(() => {
    if (serviceFilter === 'all') {
      return services;
    }
    return services.filter((service) => service.serviceUuid === serviceFilter);
  }, [serviceFilter, services]);

  const getBookingStatus = useCallback((booking) => normalizeBookingStatus(booking.status), []);

  const dateScopedBookings = useMemo(() => {
    return bookings.filter((booking) => (
      extractDateKey(booking.appointmentStartTime) === selectedDateKey
    ));
  }, [bookings, selectedDateKey]);

  // Use only real bookings from API - no mock fallback
  const sourceBookings = useMemo(
    () => dateScopedBookings,
    [dateScopedBookings]
  );

  const filteredBookings = useMemo(() => {
    return sourceBookings.filter((booking) => {
      if (serviceFilter === 'all') {
        return true;
      }
      return booking.serviceUuid === serviceFilter;
    });
  }, [serviceFilter, sourceBookings]);

  const selectedSlotDrilldown = useMemo(() => buildServiceSlotDrilldownViewModel({
    selectedSlot: selectedServiceSlot,
    bookings: dateScopedBookings.filter((booking) => bookingMatchesSearch(booking, bookingSearchQuery))
  }), [bookingSearchQuery, dateScopedBookings, selectedServiceSlot]);

  useEffect(() => {
    setSelectedServiceSlot(null);
    setBookingSearchQuery('');
    const container = timelineScrollContainerRef.current;
    if (container) {
      container.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [selectedDateKey]);

  const bookingDatesForCalendar = useMemo(() => {
    const dates = new Set();
    sourceBookings.forEach((booking) => {
      if (booking.appointmentStartTime) {
        const d = parseDateValue(booking.appointmentStartTime);
        if (d) {
          dates.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
        }
      }
    });
    return dates;
  }, [sourceBookings]);

  const bookingsListRows = useMemo(() => {
    const timelineAndStatusFiltered = filteredBookings.filter((booking) => {
      const timelineMatches = (() => {
        if (timelineFilter === 'all') {
          return true;
        }
        if (timelineFilter === 'custom') {
          return isWithinCustomTimeline(
            booking.appointmentStartTime,
            customTimelineStart,
            customTimelineEnd
          );
        }
        return resolveTimelineBucket(booking.appointmentStartTime) === timelineFilter;
      })();
      if (!timelineMatches) {
        return false;
      }
      if (appointmentStatusFilter === 'all') {
        return true;
      }
      return getBookingStatus(booking) === appointmentStatusFilter;
    });

    const searchedBookings = timelineAndStatusFiltered.filter((booking) => bookingMatchesSearch(booking, bookingSearchQuery));

    const directionFactor = bookingSort.direction === 'asc' ? 1 : -1;
    const normalizeText = (value) => String(value || '').trim().toLowerCase();

    const resolveSortValue = (booking) => {
      const normalizedStatus = getBookingStatus(booking);
      if (bookingSort.key === 'token') {
        return String(booking?.tokenNumber || booking?.token || booking?.appointmentToken || booking?.queueToken || booking?.bookingUuid || '');
      }
      if (bookingSort.key === 'time') {
        return parseDateValue(booking.appointmentStartTime)?.getTime() || 0;
      }
      if (bookingSort.key === 'duration') {
        return getDurationMinutes(booking, serviceMap);
      }
      if (bookingSort.key === 'customer') {
        return normalizeText(booking.customerName);
      }
      if (bookingSort.key === 'service') {
        return bookingServicesSearchText(booking);
      }
      if (bookingSort.key === 'status') {
        return APPOINTMENT_STATUS_SORT_ORDER[normalizedStatus] || 99;
      }
      return parseDateValue(booking.appointmentStartTime)?.getTime() || 0;
    };

    return [...searchedBookings].sort((leftBooking, rightBooking) => {
      const leftValue = resolveSortValue(leftBooking);
      const rightValue = resolveSortValue(rightBooking);

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return (leftValue - rightValue) * directionFactor;
      }

      return String(leftValue).localeCompare(String(rightValue), 'en', { numeric: true, sensitivity: 'base' }) * directionFactor;
    });
  }, [
    appointmentStatusFilter,
    bookingSort.direction,
    bookingSort.key,
    customTimelineEnd,
    customTimelineStart,
    bookingSearchQuery,
    filteredBookings,
    getBookingStatus,
    serviceMap,
    timelineFilter
  ]);

  const TIMELINE_HOURS = useMemo(() => {
    const day = selectedDate ? selectedDate.getDay() : new Date().getDay();
    const isWeekend = day === 0 || day === 6;
    const openStr = isWeekend ? settings.weekendOpen : settings.weekdayOpen;
    const closeStr = isWeekend ? settings.weekendClose : settings.weekdayClose;
    const startH = openStr ? parseInt(openStr.split(':')[0], 10) : 9;
    const endH = closeStr ? parseInt(closeStr.split(':')[0], 10) : 21;
    const hours = [];
    for (let h = startH; h <= endH; h++) {
      const ampm = h < 12 ? 'AM' : 'PM';
      const display = h === 12 ? 12 : h > 12 ? h - 12 : h;
      hours.push({ hour: h, label: `${display}:00 ${ampm}` });
    }
    return hours;
  }, [selectedDate, settings.weekdayOpen, settings.weekdayClose, settings.weekendOpen, settings.weekendClose]);

  const timelineSlots = useMemo(() => {
    const grouped = new Map();
    TIMELINE_HOURS.forEach(({ hour }) => grouped.set(hour, []));
    bookingsListRows.forEach((booking) => {
      const parsed = parseDateValue(booking.appointmentStartTime);
      if (!parsed) return;
      const h = parsed.getHours();
      if (grouped.has(h)) {
        grouped.get(h).push(booking);
      } else {
        const nearest = TIMELINE_HOURS.reduce((prev, curr) =>
          Math.abs(curr.hour - h) < Math.abs(prev.hour - h) ? curr : prev
        );
        if (grouped.has(nearest.hour)) {
          grouped.get(nearest.hour).push(booking);
        }
      }
    });
    return TIMELINE_HOURS.map(({ hour, label }) => ({
      hour,
      label,
      items: grouped.get(hour) || []
    }));
  }, [TIMELINE_HOURS, bookingsListRows]);

  useEffect(() => {
    const normalizedQuery = normalizeSearchText(bookingSearchQuery);
    if (!normalizedQuery || bookingsListRows.length === 0) {
      return;
    }

    const firstMatch = bookingsListRows[0];
    const bookingUuid = firstMatch?.bookingUuid;
    if (!bookingUuid) {
      return;
    }

    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        const target = bookingTimelineRefs.current.get(bookingUuid);
        const container = timelineScrollContainerRef.current;
        if (!target || !container) {
          return;
        }

        const relativeTop = resolveRelativeOffsetTop(target, container);
        const centeredScrollTop = relativeTop - ((container.clientHeight - target.offsetHeight) / 2);
        container.scrollTo({
          top: Math.max(0, centeredScrollTop),
          behavior: 'smooth'
        });
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [bookingSearchQuery, bookingsListRows]);

  const resolveApptVariant = useCallback((booking) => {
    const status = getBookingStatus(booking);
    if (status === 'cancelled' || status === 'missed') return 'appointments-v2-appt-ghost';
    // C2 — Variant heuristic now scans all service names on multi-service bookings.
    const svcName = bookingServicesSearchText(booking);
    if (svcName.includes('beard') || svcName.includes('trim') || svcName.includes('facial')) return 'appointments-v2-appt-alt';
    if (svcName.includes('colour') || svcName.includes('spa') || svcName.includes('massage')) return 'appointments-v2-appt-alt2';
    return '';
  }, [getBookingStatus]);

  const timelineDateLabel = useMemo(() => {
    if (!selectedDate) return '';
    const dayName = selectedDate.toLocaleDateString('en-IN', { weekday: 'long' });
    const dayNum = selectedDate.getDate();
    const month = selectedDate.toLocaleDateString('en-IN', { month: 'short' });
    const first = TIMELINE_HOURS[0];
    const last = TIMELINE_HOURS[TIMELINE_HOURS.length - 1];
    const fmtH = (h) => { const d = h === 12 ? 12 : h > 12 ? h - 12 : h; return `${d} ${h < 12 ? 'AM' : 'PM'}`; };
    const range = first && last ? ` · ${fmtH(first.hour)} – ${fmtH(last.hour)}` : '';
    return `${dayName}, ${dayNum} ${month}${range}`;
  }, [selectedDate, TIMELINE_HOURS]);

  const isPastDate = useMemo(() => {
    if (!selectedDate) return false;
    const sel = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
    if (Number.isNaN(sel.getTime())) return false;
    const todayKey = formatDateInput(new Date());
    const selKey = formatDateInput(sel);
    return selKey < todayKey;
  }, [selectedDate]);
  const isPastDateRef = useRef(false);
  useEffect(() => {
    isPastDateRef.current = isPastDate;
  }, [isPastDate]);

  const slotStats = useMemo(() => {
    const visibleServiceIds = new Set(visibleServices.map((service) => service.serviceUuid));
    const visibleSlots = Object.entries(slotsByService)
      .filter(([serviceId]) => visibleServiceIds.has(serviceId))
      .flatMap(([, slots]) => (Array.isArray(slots) ? slots : []));
    const openSlots = visibleSlots.filter((slot) => (
      slot.available !== false && Number(slot.vacanciesLeft ?? 0) > 0
    )).length;
    const filledSlots = visibleSlots.filter((slot) => (
      Number(slot.bookedCount ?? 0) > 0
      || slot.available === false
      || Number(slot.vacanciesLeft ?? 0) <= 0
    )).length;
    const totalSlots = visibleSlots.length;
    return { openSlots, filledSlots, totalSlots };
  }, [slotsByService, visibleServices]);

  const bookingStats = useMemo(() => {
    const totalBookings = filteredBookings.length;
    const completed = filteredBookings.filter((booking) => getBookingStatus(booking) === 'done').length;
    const noShows = filteredBookings.filter((booking) => getBookingStatus(booking) === 'missed').length;
    const cancellations = filteredBookings.filter((booking) => getBookingStatus(booking) === 'cancelled').length;
    const walkins = filteredBookings.filter((booking) => booking.walkin === true).length;
    const remaining = Math.max(0, totalBookings - completed - noShows);
    const openSlots = slotStats.openSlots;
    const filledSlots = slotStats.filledSlots;
    const totalSlots = slotStats.totalSlots;
    const fillRate = totalSlots > 0 ? Math.round((filledSlots / totalSlots) * 100) : 0;
    const noShowDelta = noShows - yesterdayNoShows;
    const noShowTrendPrefix = noShowDelta === 0 ? '' : noShowDelta > 0 ? '+' : '';
    const noShowTrend = `${noShowTrendPrefix} ${Math.abs(noShowDelta)} vs yesterday`;
    const selectedDay = parseDateValue(selectedDate)?.getDay() ?? 1;
    const closingTime = selectedDay === 0 || selectedDay === 6 ? settings.weekendClose : settings.weekdayClose;

    const { expectedRevenue, pricedBookings } = filteredBookings.reduce(
      (acc, booking) => {
        const service = serviceMap.get(booking.serviceUuid);
        const price = Number(service?.depositAmount);
        if (Number.isFinite(price) && price > 0) {
          acc.expectedRevenue += price;
          acc.pricedBookings += 1;
        }
        return acc;
      },
      { expectedRevenue: 0, pricedBookings: 0 }
    );
    const avgTicket = pricedBookings > 0 ? Math.round(expectedRevenue / pricedBookings) : 0;

    return {
      totalBookings,
      completed,
      noShows,
      cancellations,
      walkins,
      remaining,
      openSlots,
      fillRate,
      filledSlots,
      totalSlots,
      noShowTrend,
      closingTime: closingTime || 'closing',
      expectedRevenue,
      avgTicket
    };
  }, [filteredBookings, getBookingStatus, selectedDate, serviceMap, settings.weekdayClose, settings.weekendClose, slotStats, yesterdayNoShows]);

  const slotFillRows = useMemo(() => {
    return services
      .filter((service) => service.active !== false)
      .map((service, index) => {
      const serviceSlots = Array.isArray(slotsByService[service.serviceUuid]) ? slotsByService[service.serviceUuid] : [];
      const total = serviceSlots.length;
      const booked = filteredBookings.filter((booking) => bookingIncludesService(booking, service.serviceUuid)).length;
      return {
        serviceId: service.serviceUuid,
        serviceName: service.serviceName || 'Service',
        booked,
        total,
        color: FILL_COLORS[index % FILL_COLORS.length]
      };
    });
  }, [filteredBookings, services, slotsByService]);

  const activeServiceCount = useMemo(
    () => services.filter((service) => service.active !== false).length,
    [services]
  );

  const maxServicesAllowed = Number(settings.maxServicesAllowed || 0) > 0
    ? Number(settings.maxServicesAllowed)
    : 3;
  const serviceLimitReached = services.length >= maxServicesAllowed;

  const saveSettings = async () => {
    if (!isEditingGlobalSettings) {
      return;
    }
    if (!request || !storeToken) {
      onToast?.({ message: 'Store session not found. Please login again.', variant: 'error' });
      return;
    }
    const normalizedTimezone = String(settingsDraft.timezone || '').trim() || detectBrowserTimezone();
    const settingsPayload = {
      ...settingsDraft,
      timezone: normalizedTimezone
    };
    setIsSavingSettings(true);
    try {
      await runWithGlobalLoader(() => saveAppointmentSettings(settingsPayload));
      setSettings((previous) => ({ ...previous, ...settingsPayload }));
      onToast?.({ message: 'Appointment settings saved.', variant: 'success' });
      setIsEditingGlobalSettings(false);
      await fetchSettingsAndServices();
      await fetchSlots();
      await fetchServiceSlotSummaries();
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Unable to save settings.';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const startGlobalSettingsEdit = () => {
    setSettingsDraft({ ...settings });
    setIsEditingGlobalSettings(true);
  };

  const cancelGlobalSettingsEdit = () => {
    setSettingsDraft({ ...settings });
    setIsEditingGlobalSettings(false);
  };

  const { isDirty: isGlobalSettingsDirty } = useDirtySettings(settingsDraft, settings);

  const quickUpdateService = useCallback(async (serviceUuid, updates, successMessage = 'Service updated.') => {
    if (!serviceUuid || !request || !storeToken) {
      return;
    }
    try {
      const response = await runWithGlobalLoader(() => updateAppointmentService(serviceUuid, updates));

      const updatedService = response?.data;
      if (updatedService?.serviceUuid) {
        setServices((previous) => previous.map((service) => (
          service.serviceUuid === serviceUuid ? { ...service, ...updatedService } : service
        )));
      } else {
        setServices((previous) => previous.map((service) => (
          service.serviceUuid === serviceUuid ? { ...service, ...updates } : service
        )));
      }
      onToast?.({ message: successMessage, variant: 'success' });
      await fetchSlots();
      await fetchServiceSlotSummaries();
      return true;
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Unable to update service.';
      onToast?.({ message, variant: 'error' });
      await fetchSettingsAndServices();
      await fetchSlots();
      await fetchServiceSlotSummaries();
      return false;
    }
  }, [
    fetchServiceSlotSummaries,
    fetchSettingsAndServices,
    fetchSlots,
    onToast,
    request,
    runWithGlobalLoader,
    storeToken,
    updateAppointmentService
  ]);

  const buildServiceDraft = useCallback((service, existingDraft = {}) => {
    const fallbackScheduleHolds = persistedServiceHolds[service?.serviceUuid] ?? service?.scheduleHolds;
    return {
      durationMinutes: Number(existingDraft.durationMinutes ?? service?.appointmentDurationMins) || 30,
      capacityPerSlot: Number(existingDraft.capacityPerSlot ?? service?.maxBookingsPerSlot) || 1,
      price: existingDraft.price ?? (service?.depositAmount ?? ''),
      openTime: existingDraft.openTime ?? settings.weekdayOpen ?? '09:00',
      closeTime: existingDraft.closeTime ?? settings.weekdayClose ?? '21:00',
      extraField: existingDraft.extraField ?? '',
      active: existingDraft.active ?? (service?.active !== false),
      activeDays: Array.isArray(existingDraft.activeDays)
        ? existingDraft.activeDays
        : (Array.isArray(service?.activeDays) ? service.activeDays : WEEKDAY_CHIPS.map((chip) => chip.index)),
      scheduleHolds: Array.isArray(existingDraft.scheduleHolds)
        ? normalizeServiceScheduleHolds(existingDraft.scheduleHolds)
        : normalizeServiceScheduleHolds(fallbackScheduleHolds)
    };
  }, [persistedServiceHolds, settings.weekdayClose, settings.weekdayOpen]);

  const persistServiceHolds = useCallback(async (serviceUuid, scheduleHolds) => {
    if (!serviceUuid) {
      return;
    }
    const normalizedHolds = normalizeServiceScheduleHolds(scheduleHolds);
    const existingHolds = normalizeServiceScheduleHolds(persistedServiceHolds[serviceUuid] || []);
    await Promise.all(existingHolds
      .filter((hold) => hold.holdUuid)
      .map((hold) => deleteAppointmentHold(hold.holdUuid)));
    await Promise.all(normalizedHolds.map((hold) => {
      const dateScoped = hold.scope === 'date';
      const todayScopedTimeHold = dateScoped && hold.date === getTodayDateKey() && hold.type === 'time_window';
      if (todayScopedTimeHold) {
        return createAppointmentDayHold(serviceUuid, {
          date: hold.date,
          fromTime: hold.fromTime,
          toTime: hold.toTime,
          reason: hold.reason || null
        });
      }
      return createAppointmentHold(serviceUuid, {
        serviceUuid,
        holdType: hold.type,
        daysJson: JSON.stringify(normalizeHoldDaysForPayload(hold)),
        fromTime: hold.type === 'time_window' ? hold.fromTime : null,
        toTime: hold.type === 'time_window' ? hold.toTime : null,
        startDate: dateScoped ? hold.date : null,
        endDate: dateScoped ? hold.date : null,
        reason: hold.reason || null
      });
    }));
    const holdsResponse = await fetchAppointmentHolds();
    const refreshedHolds = Array.isArray(holdsResponse?.data) ? holdsResponse.data : [];
    const refreshedByService = refreshedHolds.reduce((acc, hold) => {
      const holdServiceUuid = hold?.serviceUuid;
      if (!holdServiceUuid) {
        return acc;
      }
      acc[holdServiceUuid] = [...(acc[holdServiceUuid] || []), normalizeServiceScheduleHold(hold)];
      return acc;
    }, {});
    setPersistedServiceHolds((previous) => {
      const next = {
        ...previous,
        ...refreshedByService,
        [serviceUuid]: refreshedByService[serviceUuid] || []
      };
      return next;
    });
    setServices((previous) => previous.map((service) => (
      service.serviceUuid === serviceUuid
        ? { ...service, scheduleHolds: refreshedByService[serviceUuid] || [] }
        : service
    )));
  }, [createAppointmentDayHold, createAppointmentHold, deleteAppointmentHold, fetchAppointmentHolds, persistedServiceHolds]);

  const clearPersistedServiceHolds = useCallback((serviceUuid) => {
    if (!serviceUuid) {
      return;
    }
    setPersistedServiceHolds((previous) => {
      const next = { ...previous };
      delete next[serviceUuid];
      return next;
    });
  }, []);

  const openServiceHoldEditor = useCallback((serviceUuid) => {
    if (!serviceUuid) {
      return;
    }
    setServiceHoldEditors((previous) => ({
      ...previous,
      [serviceUuid]: {
        ...buildEmptyServiceHoldDraft(),
        date: getTodayDateKey()
      }
    }));
  }, []);

  const closeServiceHoldEditor = useCallback((serviceUuid) => {
    if (!serviceUuid) {
      return;
    }
    setServiceHoldEditors((previous) => {
      const next = { ...previous };
      delete next[serviceUuid];
      return next;
    });
  }, []);

  const updateServiceHoldEditor = useCallback((serviceUuid, updates) => {
    if (!serviceUuid) {
      return;
    }
    setServiceHoldEditors((previous) => ({
      ...previous,
      [serviceUuid]: {
        ...(previous[serviceUuid] || buildEmptyServiceHoldDraft()),
        ...updates
      }
    }));
  }, []);

  const toggleServiceHoldEditorDay = useCallback((serviceUuid, dayIndex) => {
    if (!serviceUuid) {
      return;
    }
    setServiceHoldEditors((previous) => {
      const currentEditor = previous[serviceUuid] || buildEmptyServiceHoldDraft();
      const currentDays = normalizeScheduleHoldDays(currentEditor.days);
      const nextDays = currentDays.includes(dayIndex)
        ? currentDays.filter((value) => value !== dayIndex)
        : [...currentDays, dayIndex];
      return {
        ...previous,
        [serviceUuid]: {
          ...currentEditor,
          days: normalizeScheduleHoldDays(nextDays)
        }
      };
    });
  }, []);

  const addServiceHoldToDraft = useCallback((service) => {
    const serviceUuid = service?.serviceUuid;
    if (!serviceUuid || editingServiceCardId !== serviceUuid) {
      return;
    }
    const holdEditor = serviceHoldEditors[serviceUuid] || buildEmptyServiceHoldDraft();
    const currentDraft = serviceDrafts[serviceUuid] || {};
    const appointmentOpenTime = currentDraft.openTime || service?.openTime || settings.weekdayOpen || '09:00';
    const appointmentCloseTime = currentDraft.closeTime || service?.closeTime || settings.weekdayClose || '21:00';
    const candidate = normalizeServiceScheduleHold({
      ...holdEditor,
      id: createScheduleHoldId(),
      type: 'time_window',
      scope: 'date',
      date: getTodayDateKey()
    });
    const holdError = getScheduleHoldValidationError(candidate, {
      enforceTodayOnly: true,
      appointmentOpenTime,
      appointmentCloseTime
    });
    if (holdError) {
      onToast?.({ message: holdError, variant: 'error' });
      return;
    }
    setServiceDrafts((previous) => {
      const currentDraft = previous[serviceUuid] || {};
      const currentHolds = normalizeServiceScheduleHolds(currentDraft.scheduleHolds);
      return {
        ...previous,
        [serviceUuid]: {
          ...currentDraft,
          scheduleHolds: [...currentHolds, candidate]
        }
      };
    });
    closeServiceHoldEditor(serviceUuid);
  }, [closeServiceHoldEditor, editingServiceCardId, onToast, serviceDrafts, serviceHoldEditors, settings.weekdayClose, settings.weekdayOpen]);

  const removeServiceHoldFromDraft = useCallback((serviceUuid, holdId) => {
    if (!serviceUuid || !holdId || editingServiceCardId !== serviceUuid) {
      return;
    }
    setServiceDrafts((previous) => {
      const currentDraft = previous[serviceUuid] || {};
      const currentHolds = normalizeServiceScheduleHolds(currentDraft.scheduleHolds);
      return {
        ...previous,
        [serviceUuid]: {
          ...currentDraft,
          scheduleHolds: currentHolds.filter((hold) => hold.id !== holdId)
        }
      };
    });
  }, [editingServiceCardId]);

  const beginServiceCardEdit = (service) => {
    if (!service?.serviceUuid) {
      return;
    }
    if (editingServiceCardId && editingServiceCardId !== service.serviceUuid) {
      onToast?.({ message: 'Save or cancel the current service changes before editing another service.', variant: 'error' });
      return;
    }
    const existingDraft = serviceDrafts[service.serviceUuid] || {};
    const editBaseline = buildServiceDraft(service, existingDraft);
    setServiceDrafts((previous) => ({
      ...previous,
      [service.serviceUuid]: editBaseline
    }));
    setServiceEditBaselines((previous) => ({
      ...previous,
      [service.serviceUuid]: editBaseline
    }));
    closeServiceHoldEditor(service.serviceUuid);
    setEditingServiceCardId(service.serviceUuid);
  };

  const cancelServiceCardEdit = (service) => {
    if (!service?.serviceUuid) {
      return;
    }
    const baseline = serviceEditBaselines[service.serviceUuid] || buildServiceDraft(service, serviceDrafts[service.serviceUuid] || {});
    setServiceDrafts((previous) => ({
      ...previous,
      [service.serviceUuid]: baseline
    }));
    setServiceEditBaselines((previous) => {
      const next = { ...previous };
      delete next[service.serviceUuid];
      return next;
    });
    closeServiceHoldEditor(service.serviceUuid);
    setEditingServiceCardId((previous) => (previous === service.serviceUuid ? '' : previous));
  };

  const saveServiceCardEdit = async (service) => {
    if (!service?.serviceUuid) {
      return;
    }
    if (editingServiceCardId !== service.serviceUuid) {
      return;
    }
    const draft = serviceDrafts[service.serviceUuid] || {};
    const holdEditor = serviceHoldEditors[service.serviceUuid] || null;
    if (holdEditor) {
      onToast?.({ message: 'Finish adding the hold or cancel it before saving the service.', variant: 'error' });
      return;
    }
    const isVirtualService = String(service.appointmentMode || '').toUpperCase() === 'VIRTUAL';
    const scheduleHolds = normalizeServiceScheduleHolds(draft.scheduleHolds);
    const serviceValidationError = getServiceDraftValidationError({ draft: { ...draft, scheduleHolds }, isVirtualService });
    if (serviceValidationError) {
      onToast?.({ message: serviceValidationError, variant: 'error' });
      return;
    }
    const holdDraftValidationError = holdEditor ? getScheduleHoldValidationError(holdEditor) : '';
    if (holdDraftValidationError) {
      onToast?.({ message: holdDraftValidationError, variant: 'error' });
      return;
    }
    const nextDeposit = String(
      (isVirtualService
        ? (draft.extraField ?? draft.price)
        : draft.price) || ''
    ).trim() || null;
    const didSave = await quickUpdateService(
      service.serviceUuid,
      {
        appointmentDurationMins: Number(draft.durationMinutes) || 30,
        maxBookingsPerSlot: Number(draft.capacityPerSlot) || 1,
        activeDays: Array.isArray(draft.activeDays) ? draft.activeDays : WEEKDAY_CHIPS.map((chip) => chip.index),
        depositAmount: nextDeposit,
        active: draft.active !== false
      },
      `${service.serviceName || 'Service'} settings saved.`
    );
    if (didSave) {
      await persistServiceHolds(service.serviceUuid, scheduleHolds);
      setServiceEditBaselines((previous) => {
        const next = { ...previous };
        delete next[service.serviceUuid];
        return next;
      });
      closeServiceHoldEditor(service.serviceUuid);
      setEditingServiceCardId((previous) => (previous === service.serviceUuid ? '' : previous));
    }
  };

  const openServiceEditor = (service) => {
    if (service) {
      setEditingServiceUuid(service.serviceUuid);
      setServiceForm({
        serviceName: service.serviceName || '',
        serviceDescription: service.serviceDescription || '',
        appointmentMode: service.appointmentMode || 'IN_STORE',
        appointmentDurationMins: Number(service.appointmentDurationMins) || 30,
        maxBookingsPerSlot: Number(service.maxBookingsPerSlot) || 1,
        depositAmount: service.depositAmount || ''
      });
    } else {
      setEditingServiceUuid('');
      setServiceForm(EMPTY_SERVICE_FORM);
    }
    setShowServiceEditor(true);
  };

  const closeServiceEditor = () => {
    setShowServiceEditor(false);
    setEditingServiceUuid('');
    setServiceForm(EMPTY_SERVICE_FORM);
  };

  const saveService = async () => {
    if (!request || !storeToken) {
      onToast?.({ message: 'Store session not found. Please login again.', variant: 'error' });
      return;
    }
    if (!String(serviceForm.serviceName || '').trim()) {
      onToast?.({ message: 'Service name is required.', variant: 'error' });
      return;
    }
    if (!editingServiceUuid && serviceLimitReached) {
      onToast?.({ message: `Service limit reached (${maxServicesAllowed}).`, variant: 'error' });
      return;
    }
    setIsSavingService(true);
    try {
      const payload = {
        serviceName: String(serviceForm.serviceName || '').trim(),
        serviceDescription: String(serviceForm.serviceDescription || '').trim() || null,
        appointmentMode: serviceForm.appointmentMode || 'IN_STORE',
        appointmentDurationMins: Number(serviceForm.appointmentDurationMins) || 30,
        maxBookingsPerSlot: Number(serviceForm.maxBookingsPerSlot) || 1,
        depositAmount: String(serviceForm.depositAmount || '').trim() || null
      };
      if (editingServiceUuid) {
        await runWithGlobalLoader(() => updateAppointmentService(editingServiceUuid, payload));
        onToast?.({ message: 'Service updated.', variant: 'success' });
      } else {
        await runWithGlobalLoader(() => createAppointmentService(payload));
        onToast?.({ message: 'Service added.', variant: 'success' });
      }
      closeServiceEditor();
      await fetchSettingsAndServices();
      await fetchSlots();
      await fetchServiceSlotSummaries();
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Unable to save service.';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsSavingService(false);
    }
  };

  const confirmDeleteService = async () => {
    const serviceUuid = deleteServiceTarget?.serviceUuid;
    if (!serviceUuid) {
      return;
    }
    if (isDeletingService) {
      return;
    }
    if (!request || !storeToken) {
      onToast?.({ message: 'Store session not found. Please login again.', variant: 'error' });
      return;
    }
    setIsDeletingService(true);
    try {
      await runWithGlobalLoader(() => deleteAppointmentService(serviceUuid));
      // Prevent follow-up calls (like slot fetches) from using a now-deleted serviceUuid.
      setServices((previous) => previous.filter((service) => service.serviceUuid !== serviceUuid));
      clearPersistedServiceHolds(serviceUuid);
      setServiceDrafts((previous) => {
        const next = { ...previous };
        delete next[serviceUuid];
        return next;
      });
      setServiceEditBaselines((previous) => {
        const next = { ...previous };
        delete next[serviceUuid];
        return next;
      });
      setServiceHoldEditors((previous) => {
        const next = { ...previous };
        delete next[serviceUuid];
        return next;
      });
      setEditingServiceCardId((previous) => (previous === serviceUuid ? '' : previous));
      setDeleteServiceTarget(null);
      setServiceSlotSummariesByService((previous) => {
        const next = { ...previous };
        delete next[serviceUuid];
        return next;
      });
      onToast?.({ message: 'Service deleted.', variant: 'success' });
      await fetchSettingsAndServices();
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Unable to delete service.';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsDeletingService(false);
    }
  };

  if (!storeToken) {
    return null;
  }

  const showBootstrapSkeleton = (!hasSettingsLoaded || !hasBookingsLoaded) && !bootstrapError;
  const showBootstrapError = Boolean(bootstrapError) && services.length === 0 && bookings.length === 0;

  const retryBootstrap = () => {
    setBootstrapError('');
    setHasSettingsLoaded(false);
    setHasBookingsLoaded(false);
    fetchSettingsAndServices();
    fetchBookings();
    fetchSlots();
    fetchServiceSlotSummaries();
  };

  const headerBlock = (
    <div className="appointments-v2-header">
      <div>
        <h2>{headerTitle}</h2>
        <p className="appointments-v2-subtitle">{formatDateLabel(selectedDate, timezone)}</p>
        {showWorkspaceTabs ? (
          <div className="appointments-v2-tabs" role="tablist" aria-label="Appointment settings workspace">
            <button
              type="button"
              className={`appointments-v2-tab ${innerTab === 'today' ? 'active' : ''}`}
              onClick={() => setInnerTab('today')}
              role="tab"
              aria-selected={innerTab === 'today'}
            >
              Today schedule
            </button>
            <button
              type="button"
              className={`appointments-v2-tab ${innerTab === 'schedule' ? 'active' : ''}`}
              onClick={() => setInnerTab('schedule')}
              role="tab"
              aria-selected={innerTab === 'schedule'}
            >
              Availability &amp; booking
            </button>
          </div>
        ) : null}
      </div>
      <div className="appointments-v2-header-actions">
        {innerTab === 'today' && (
          <div className="appointments-v2-segmented">
            <button type="button" className={`appointments-v2-segmented-btn ${calendarView === 'day' ? 'active' : ''}`} onClick={() => setCalendarView('day')}>Day</button>
            <button type="button" className={`appointments-v2-segmented-btn ${calendarView === 'week' ? 'active' : ''}`} onClick={() => setCalendarView('week')}>Week</button>
            <button type="button" className={`appointments-v2-segmented-btn ${calendarView === 'month' ? 'active' : ''}`} onClick={() => setCalendarView('month')}>Month</button>
          </div>
        )}
      </div>
    </div>
  );

  if (showBootstrapSkeleton || showBootstrapError) {
    return (
      <ErrorBoundary level="feature">
        <div className="store-manage-container appointments-v2-root">
          {headerBlock}
          {showBootstrapSkeleton ? (
            <AppointmentsLoadingSkeleton />
          ) : (
            <section className="appointments-v2-section">
              <EmptyState
                icon={<AlertTriangle size={20} />}
                title="Couldn't load appointments data"
                description="There was a problem fetching appointments information. Try refreshing this section."
                action={{ label: 'Retry', onClick: retryBootstrap }}
              />
            </section>
          )}
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary level="feature">
    <div className="store-manage-container appointments-v2-root">
      {headerBlock}

      {/* CP-97 — the one number that proves Zopeo pays for itself. */}
      {roiSummary && innerTab === 'today' ? (
        <div className="appointments-v2-roi-banner">
          <div className="appointments-v2-roi-main">
            <span className="appointments-v2-roi-label">Zopeo brought you</span>
            <span className="appointments-v2-roi-value">
              ₹{new Intl.NumberFormat('en-IN').format(Math.round(Number(roiSummary.totalRevenuePaise || 0) / 100))}
            </span>
            <span className="appointments-v2-roi-meta">in completed visits · {roiSummary.monthLabel}</span>
          </div>
          <div className="appointments-v2-roi-chips">
            <span className="appointments-v2-roi-chip">{Number(roiSummary.completedBookings || 0)} bookings completed</span>
            <span className="appointments-v2-roi-chip">{Number(roiSummary.completedWalkins || 0)} walk-ins served</span>
            <span className="appointments-v2-roi-chip">{Number(roiSummary.offersRedeemed || 0)} offers redeemed</span>
            {Number(roiSummary.offerAttributedRevenueRupees || 0) > 0 ? (
              <span className="appointments-v2-roi-chip appointments-v2-roi-chip-accent">
                ₹{new Intl.NumberFormat('en-IN').format(Number(roiSummary.offerAttributedRevenueRupees))} offer-driven all time
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* CP-111: unpublished-draft nudge sits right under the ROI headline
          (user decision) — the money line first, then the money left on the table. */}
      {onReviewDrafts && innerTab === 'today' ? (
        <DraftNudgeBanner request={request} onReviewDrafts={onReviewDrafts} />
      ) : null}

      <BookingCalendar visible={innerTab === 'today'}>
        <section className="appointments-v2-section">
          <div className="appointments-v2-layout">
            <div className="appointments-v2-calendar-col">
              <MiniCalendar
                selectedDate={selectedDate}
                onDateSelect={setSelectedDate}
                bookingDates={bookingDatesForCalendar}
              />
              <button
                type="button"
                className="appointments-v2-history-btn"
                onClick={openHistoryModal}
              >
                <Download size={14} aria-hidden="true" />
                <span>Download history</span>
              </button>
            </div>
            <div className="appointments-v2-main">
              <BookingList visible={true}>
              <article className="appointments-v2-tline">
                <div className="appointments-v2-tline-header">
                  <div className="appointments-v2-tline-toprow">
                    <h3>{timelineDateLabel}</h3>
                    <div className="appointments-v2-tline-actions">
                      <button
                        className="btn primary"
                        onClick={() => {
                          if (isPastDate) {
                            onToast?.({ message: 'Cannot add walk-ins for past dates.', variant: 'error' });
                            return;
                          }
                          setWalkinModalOpen(true);
                        }}
                        disabled={!services.length || activeRequestCount > 0 || isPastDate}
                        aria-disabled={isPastDate ? 'true' : undefined}
                        title={isPastDate ? 'Cannot add walk-ins for past dates' : undefined}
                        style={{
                          fontSize: '13px',
                          padding: '8px 16px',
                          ...(isPastDate ? { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' } : {}),
                        }}
                      >
                        + Add Walk-In
                      </button>
                      <div className="appointments-v2-tline-live">
                        <span className="appointments-v2-tline-live-dot" />
                        <span>live</span>
                      </div>
                    </div>
                  </div>
                  <label className="appointments-v2-search-shell" aria-label="Search appointments by name or phone number">
                    <input
                      type="search"
                      className="appointments-v2-search-input"
                      value={bookingSearchQuery}
                      onChange={(event) => setBookingSearchQuery(event.target.value)}
                      placeholder="Search by customer name or phone number"
                    />
                    {bookingSearchQuery ? (
                      <button
                        type="button"
                        className="appointments-v2-search-clear"
                        onClick={() => setBookingSearchQuery('')}
                        aria-label="Clear appointment search"
                      >
                        Clear
                      </button>
                    ) : null}
                  </label>
                </div>
                <div className="appointments-v2-tline-rows" ref={timelineScrollContainerRef}>
                  {timelineSlots.map((slot) => (
                    <div className="appointments-v2-tline-row" key={slot.hour}>
                      <div className="appointments-v2-tline-hour">{slot.label}</div>
                      <div className="appointments-v2-tline-col">
                        {slot.items.length === 0 ? (
                          <div className="appointments-v2-tline-empty">—</div>
                        ) : (
                          slot.items.map((booking, idx) => {
                            const duration = getDurationMinutes(booking, serviceMap);
                            const variant = resolveApptVariant(booking);
                            const status = getBookingStatus(booking);
                            const isWalkin = booking.walkin === true;
                            const bookingSourceBadge = isWalkin
                              ? { label: 'Walk-in', className: 'booking-source-badge-walkin' }
                              : { label: 'App', className: 'booking-source-badge-app' };
                            const isTerminal = ['done', 'missed', 'cancelled'].includes(status);
                            return (
                              <div
                                className={`appointments-v2-appt ${variant}`}
                                key={booking.bookingUuid || idx}
                                ref={(node) => {
                                  if (!booking?.bookingUuid) {
                                    return;
                                  }
                                  if (node) {
                                    bookingTimelineRefs.current.set(booking.bookingUuid, node);
                                  } else {
                                    bookingTimelineRefs.current.delete(booking.bookingUuid);
                                  }
                                }}
                                onClick={() => openAppointmentDetails(booking)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    openAppointmentDetails(booking);
                                  }
                                }}
                              >
                                <div>
                                  <div className="appointments-v2-appt-header">
                                    <div className="appointments-v2-appt-name">{booking.customerName || 'Customer'}</div>
                                    <span className={`booking-source-badge ${bookingSourceBadge.className}`}>
                                      {bookingSourceBadge.label}
                                    </span>
                                  </div>
                                  <div className="appointments-v2-appt-phone">{booking.customerPhone || 'No phone added'}</div>
                                  <div className="appointments-v2-appt-meta">{`${formatBookingServices(booking)}${booking.notes ? ` · ${booking.notes}` : ''}`}</div>
                                  <AppointmentOfferChip booking={booking} />
                                  <AppointmentMembershipChip booking={booking} />
                            <AppointmentLoyaltyRewardChip booking={booking} />
                                  <AppointmentLoyaltyRewardChip booking={booking} />
                                </div>
                                <span className="appointments-v2-appt-slot-chip">
                                  {`${formatTime(booking.appointmentStartTime)} - ${formatTime(booking.appointmentEndTime)} · ${duration}m`}
                                </span>
                                <div className="appointments-v2-appt-footer">
                                  <span className={`appointments-v2-slot-drilldown-status ${status}`}>
                                    {statusBadgeLabel(booking.status)}
                                  </span>
                                  <select
                                    className="appointments-v2-appt-status-select"
                                    value={status}
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={(e) => handleStatusChange(booking, e.target.value)}
                                    disabled={isPastDate}
                                    title={isPastDate ? 'Status cannot be updated for past dates' : undefined}
                                  >
                                    {['pending', 'confirmed', 'in_progress', 'done', 'missed', 'cancelled'].map(s => {
                                      const allowed = BOOKING_STATUS_TRANSITIONS[status] || [];
                                      return (
                                        <option key={s} value={s} disabled={s !== status && !allowed.includes(s)}>
                                          {statusBadgeLabel(s)}
                                        </option>
                                      );
                                    })}
                                  </select>
                                  {isWalkin && (
                                    <div className="appointments-v2-booking-actions">
                                      {!isTerminal && (
                                        <button
                                          className="appointments-v2-btn appointments-v2-btn-small"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleEditWalkinClick(booking);
                                          }}
                                          type="button"
                                        >
                                          <Edit2 size={12} /> Edit
                                        </button>
                                      )}
                                      {!isTerminal && (
                                        <button
                                          className="appointments-v2-btn appointments-v2-btn-small"
                                          disabled={isWalkinOfferMutating}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            if (booking.offerId != null || booking.offerCode) {
                                              detachOfferFromWalkinBooking(booking);
                                            } else if (booking.loyaltyRewardUuid) {
                                              detachLoyaltyRewardFromWalkinBooking(booking);
                                            } else if (booking.membershipUuid) {
                                              detachMembershipFromWalkinBooking(booking);
                                            } else {
                                              openWalkinOfferPicker(booking);
                                            }
                                          }}
                                          type="button"
                                        >
                                          <Tag size={12} /> {booking.offerId != null || booking.offerCode
                                            ? 'Remove offer'
                                            : booking.loyaltyRewardUuid ? 'Remove reward'
                                              : booking.membershipUuid ? 'Remove membership' : 'Benefit'}
                                        </button>
                                      )}
                                      <button
                                        className="appointments-v2-btn appointments-v2-btn-small appointments-v2-btn-danger"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          setDeleteWalkinTarget(booking);
                                        }}
                                        type="button"
                                      >
                                        <Trash2 size={12} /> Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </article>
              </BookingList>
            </div>

            <aside className="appointments-v2-side">
              <SlotManager visible={true}>
              <article className="appointments-v2-card appointments-v2-day-glance">
                <h3 className="appointments-v2-day-glance-title">Day at a glance</h3>
                <div className="appointments-v2-day-glance-rows">
                  <div className="appointments-v2-day-glance-row">
                    <span className="appointments-v2-day-glance-label">Bookings</span>
                    <span className="appointments-v2-day-glance-value">{bookingStats.totalBookings}</span>
                  </div>
                  <div className="appointments-v2-day-glance-row">
                    <span className="appointments-v2-day-glance-label">Walk-ins</span>
                    <span className="appointments-v2-day-glance-value">{bookingStats.walkins}</span>
                  </div>
                  <div className="appointments-v2-day-glance-row">
                    <span className="appointments-v2-day-glance-label">Cancellations</span>
                    <span className="appointments-v2-day-glance-value appointments-v2-day-glance-amber">{bookingStats.cancellations}</span>
                  </div>
                  <div className="appointments-v2-day-glance-row">
                    <span className="appointments-v2-day-glance-label">Missed</span>
                    <span className="appointments-v2-day-glance-value appointments-v2-day-glance-red">{bookingStats.noShows}</span>
                  </div>
                  <div className="appointments-v2-day-glance-row">
                    <span className="appointments-v2-day-glance-label">Expected revenue</span>
                    <span className="appointments-v2-day-glance-value appointments-v2-day-glance-green">
                      {bookingStats.expectedRevenue > 0
                        ? `₹${bookingStats.expectedRevenue.toLocaleString('en-IN')}`
                        : '—'}
                    </span>
                  </div>
                  <div className="appointments-v2-day-glance-row">
                    <span className="appointments-v2-day-glance-label">Avg ticket</span>
                    <span className="appointments-v2-day-glance-value">
                      {bookingStats.avgTicket > 0
                        ? `₹${bookingStats.avgTicket.toLocaleString('en-IN')}`
                        : '—'}
                    </span>
                  </div>
                </div>
                <div className="appointments-v2-day-glance-divider" />
                <h3 className="appointments-v2-day-glance-subtitle">Staff utilisation</h3>
                {slotFillRows.length === 0 && <div className="appointments-v2-empty">No services active.</div>}
                {slotFillRows.map((row) => {
                  const ratio = row.total > 0 ? Math.min(100, Math.round((row.booked / row.total) * 100)) : 0;
                  const barColor = ratio >= 80 ? '#31F08C' : ratio >= 50 ? '#fbbf24' : '#5a6578';
                  return (
                    <div className="appointments-v2-util-row" key={row.serviceId}>
                      <div className="appointments-v2-util-head">
                        <span className="appointments-v2-util-name">{row.serviceName}</span>
                        <span className="appointments-v2-util-pct">{`${ratio}%`}</span>
                      </div>
                      <div className="appointments-v2-util-track">
                        <div className="appointments-v2-util-bar" style={{ width: `${ratio}%`, backgroundColor: barColor }} />
                      </div>
                    </div>
                  );
                })}
              </article>
              </SlotManager>
            </aside>
          </div>
        </section>
      </BookingCalendar>

      <AppointmentSettings visible={innerTab === 'schedule'}>
        <section className="appointments-v2-section">
          <article className="appointments-v2-card appointments-v2-global-settings">
            <BookingRulesHeader
              isEditing={isEditingGlobalSettings}
              isDirty={isGlobalSettingsDirty}
              isSaving={isSavingSettings}
              canSave={!hasInvalidGlobalTiming && cancellationWindowValidation.valid && rescheduleWindowValidation.valid}
              onEdit={startGlobalSettingsEdit}
              onCancel={cancelGlobalSettingsEdit}
              onSave={saveSettings}
            />

            <OperatingHoursSection
              weekdayOpen={settingsDraft.weekdayOpen}
              weekdayClose={settingsDraft.weekdayClose}
              weekendOpen={settingsDraft.weekendOpen}
              weekendClose={settingsDraft.weekendClose}
              disabled={!isEditingGlobalSettings}
              onTimeChange={handleSettingsTimeChange}
            />

            <div className="appointments-v3-rules-grid">
            <div className="appointments-v3-reminder-field">
              <div className="appointments-v3-reminder-field-head">
                <label className="appointments-v3-reminder-field-label">REMIND CUSTOMER</label>
              </div>
              <ReminderPillGroup
                options={REMINDER_OPTIONS}
                selectedKeys={selectedReminderOffsets}
                disabled={!isEditingGlobalSettings}
                onToggle={(key) => {
                  if (!isEditingGlobalSettings) return;
                  const next = new Set(selectedReminderOffsets);
                  if (next.has(key)) {
                    next.delete(key);
                  } else {
                    next.add(key);
                  }
                  setSettingsDraft((previous) => ({ ...previous, reminderOffsets: formatReminderOffsets(next) }));
                }}
              />
              <p className="appointments-v3-reminder-field-subtitle">
                Automated reminders are sent before each appointment. <strong>{formatSelectedSubtitle(selectedReminderOffsets.size)}</strong>.
              </p>
            </div>

            <div className="appointments-v3-window-field">
              <label className="appointments-v3-window-field-label">RESCHEDULE WINDOW</label>
              <StepperInput
                value={settingsDraft.rescheduleWindowHours ?? null}
                min={0}
                max={168}
                disabled={!isEditingGlobalSettings}
                ariaLabel="Reschedule window hours"
                allowTyping
                variant="split"
                onChange={(next) => setSettingsDraft((previous) => ({
                  ...previous,
                  rescheduleWindowHours: next,
                }))}
                onBlur={() => {
                  if (settingsDraft.rescheduleWindowHours == null) {
                    setSettingsDraft((previous) => ({
                      ...previous,
                      rescheduleWindowHours: 4,
                    }));
                  }
                }}
              />
              {(() => {
                const hours = settingsDraft.rescheduleWindowHours;
                if (hours == null || hours === '') return null;
                let message = '';
                if (hours === 0) {
                  message = 'Customers can reschedule anytime before their appointment.';
                } else if (hours === 1) {
                  message = 'Customers must reschedule at least 1 hour before their appointment.';
                } else if (hours <= 23) {
                  message = `Customers must reschedule at least ${hours} hours before their appointment.`;
                } else if (hours === 24) {
                  message = 'Customers must reschedule at least 1 day before their appointment.';
                } else if (hours % 24 === 0) {
                  message = `Customers must reschedule at least ${hours / 24} days before their appointment.`;
                } else {
                  message = `Customers must reschedule at least ${hours} hours (${Math.floor(hours / 24)} days, ${hours % 24} hours) before their appointment.`;
                }
                return (
                  <p className="appointments-v3-window-field-explainer">{message}</p>
                );
              })()}
              {rescheduleWindowValidation.error && (
                <p className="appointments-v3-window-field-error">
                  ⚠️ {rescheduleWindowValidation.error}
                </p>
              )}
              {rescheduleWindowValidation.warning && !rescheduleWindowValidation.error && (
                <p className="appointments-v3-window-field-warning">
                  ⚠️ {rescheduleWindowValidation.warning}
                </p>
              )}
            </div>

            <div className="appointments-v3-window-field">
              <label className="appointments-v3-window-field-label">CANCELLATION WINDOW</label>
              <StepperInput
                value={settingsDraft.cancellationWindowHours ?? null}
                min={0}
                max={168}
                disabled={!isEditingGlobalSettings}
                ariaLabel="Cancellation window hours"
                allowTyping
                variant="split"
                onChange={(next) => setSettingsDraft((previous) => ({
                  ...previous,
                  cancellationWindowHours: next,
                }))}
                onBlur={() => {
                  if (settingsDraft.cancellationWindowHours == null) {
                    setSettingsDraft((previous) => ({
                      ...previous,
                      cancellationWindowHours: 4,
                    }));
                  }
                }}
              />
              {settingsDraft.cancellationWindowHours === 0 ? (
                <CancellationCallout />
              ) : (
                (() => {
                  const hours = settingsDraft.cancellationWindowHours;
                  if (hours == null || hours === '') return null;
                  let message = '';
                  if (hours === 1) {
                    message = 'Customers must cancel at least 1 hour before their appointment.';
                  } else if (hours <= 23) {
                    message = `Customers must cancel at least ${hours} hours before their appointment.`;
                  } else if (hours === 24) {
                    message = 'Customers must cancel at least 1 day before their appointment.';
                  } else if (hours % 24 === 0) {
                    message = `Customers must cancel at least ${hours / 24} days before their appointment.`;
                  } else {
                    message = `Customers must cancel at least ${hours} hours (${Math.floor(hours / 24)} days, ${hours % 24} hours) before their appointment.`;
                  }
                  return (
                    <p className="appointments-v3-window-field-explainer">{message}</p>
                  );
                })()
              )}
              {cancellationWindowValidation.error && settingsDraft.cancellationWindowHours !== 0 && (
                <p className="appointments-v3-window-field-error">
                  ⚠️ {cancellationWindowValidation.error}
                </p>
              )}
              {cancellationWindowValidation.warning && !cancellationWindowValidation.error && settingsDraft.cancellationWindowHours !== 0 && (
                <p className="appointments-v3-window-field-warning">
                  ⚠️ {cancellationWindowValidation.warning}
                </p>
              )}
              <p className="appointments-v3-window-field-note">
                In hours · 2 = 2h, 24 = 1 day, 168 = 7 days.
              </p>
            </div>
            </div>

            {/* G1 — Booking policies (multi-service) */}
            <MultiServiceRow
              enabled={settingsDraft.multiServiceBookingsEnabled !== false}
              maxAllowed={settingsDraft.maxServicesAllowed}
              disabled={!isEditingGlobalSettings}
              onEnabledChange={(next) => setSettingsDraft((previous) => ({
                ...previous,
                multiServiceBookingsEnabled: next,
              }))}
              onMaxChange={(next) => setSettingsDraft((previous) => ({
                ...previous,
                maxServicesAllowed: next,
              }))}
              onMaxBlur={() => {
                const v = settingsDraft.maxServicesAllowed;
                if (v == null || v < 1) {
                  setSettingsDraft((previous) => ({ ...previous, maxServicesAllowed: 3 }));
                } else if (v > 10) {
                  setSettingsDraft((previous) => ({ ...previous, maxServicesAllowed: 10 }));
                }
              }}
            />
          </article>

          <ServiceManager visible={true}>
          <article className="appointments-v2-card appointments-v2-services-wrapper">
            <ServicesHeader activeCount={activeServiceCount} totalCount={services.length} />

            <div className="appointments-v2-service-grid">
              {services.map((service) => {
                const draft = serviceDrafts[service.serviceUuid] || {};
                const activeDays = Array.isArray(draft.activeDays) ? draft.activeDays : WEEKDAY_CHIPS.map((chip) => chip.index);
                const isServiceEditing = editingServiceCardId === service.serviceUuid;
                const isActive = isServiceEditing ? draft.active !== false : service.active !== false;
                const isVirtualService = String(service.appointmentMode || '').toUpperCase() === 'VIRTUAL';
                const scheduleHolds = normalizeServiceScheduleHolds(draft.scheduleHolds);
                const todayDateKey = getTodayDateKey();
                const todaysHolds = scheduleHolds
                  .filter((hold) => hold.type === 'time_window'
                    && hold.scope === 'date'
                    && hold.date === todayDateKey
                    && hold.fromTime
                    && hold.toTime)
                  .sort((a, b) => (parseTimeToMinutes(a.fromTime) ?? 0) - (parseTimeToMinutes(b.fromTime) ?? 0));
                const holdBadge = todaysHolds.length > 0
                  ? {
                    fromTime: todaysHolds[0].fromTime,
                    toTime: todaysHolds[0].toTime,
                    extraCount: todaysHolds.length - 1
                  }
                  : null;
                const serviceValidationError = getServiceDraftValidationError({ draft: { ...draft, scheduleHolds }, isVirtualService });
                const holdEditor = serviceHoldEditors[service.serviceUuid] || null;
                const holdEditorValidationError = holdEditor ? getScheduleHoldValidationError({
                  ...holdEditor,
                  type: 'time_window',
                  scope: 'date',
                  date: getTodayDateKey()
                }, {
                  enforceTodayOnly: true,
                  appointmentOpenTime: draft.openTime || service.openTime || settings.weekdayOpen || '09:00',
                  appointmentCloseTime: draft.closeTime || service.closeTime || settings.weekdayClose || '21:00'
                }) : '';
                const hasHoldEditor = Boolean(holdEditor);
                const hasInvalidServiceDraft = Boolean(serviceValidationError);
                const hasInvalidHoldDraft = Boolean(holdEditorValidationError);
                const disableServiceSave = hasInvalidServiceDraft || hasInvalidHoldDraft || hasHoldEditor;
                const scheduleHoldGroups = SERVICE_HOLD_TYPES
                  .map((holdType) => ({
                    key: holdType.key,
                    label: holdType.label,
                    items: scheduleHolds.filter((hold) => normalizeScheduleHoldType(hold?.type) === holdType.key)
                  }))
                  .filter((group) => group.items.length > 0);
                const serviceSlots = Array.isArray(slotsByService[service.serviceUuid]) ? slotsByService[service.serviceUuid] : [];
                const serviceSlotSummary = serviceSlotSummariesByService[service.serviceUuid] || null;
                const slotPreviewView = buildServiceSlotPreviewViewModel({
                  service,
                  slots: serviceSlots,
                  bookings: filteredBookings,
                  summary: serviceSlotSummary,
                  maxVisibleSlots: Number.POSITIVE_INFINITY
                });
                const bookedCount = filteredBookings.filter((booking) => bookingIncludesService(booking, service.serviceUuid)).length;
                const openCount = Math.max(0, serviceSlots.length - bookedCount);

                return (
                  <div
                    key={service.serviceUuid}
                    className={`appointments-v2-service-card ${isActive ? 'active' : 'disabled'} ${isServiceEditing ? 'is-editing' : 'is-readonly'}`}
                    data-slot-summary-status={serviceSlotSummary?.status || ''}
                  >
                    <ServiceTileHead
                      service={service}
                      isActive={isActive}
                      durationMinutes={draft.durationMinutes ?? service.appointmentDurationMins}
                      capacityPerSlot={draft.capacityPerSlot ?? service.maxBookingsPerSlot}
                      price={draft.price ?? service.depositAmount}
                      holdBadge={holdBadge}
                      disabled={isServiceEditing}
                      onEdit={isServiceEditing ? undefined : () => beginServiceCardEdit(service)}
                      onMenu={() => openServiceEditor(service)}
                    />

                    {!isServiceEditing && (() => {
                      const openTimeRaw = draft.openTime || service.openTime || settings.weekdayOpen || '09:00';
                      const closeTimeRaw = draft.closeTime || service.closeTime || settings.weekdayClose || '21:00';
                      const openMin = parseTimeToMinutes(openTimeRaw);
                      const closeMin = parseTimeToMinutes(closeTimeRaw);
                      let timeline = null;
                      if (openMin != null && closeMin != null && closeMin > openMin) {
                        const openHour = Math.floor(openMin / 60);
                        const closeHour = Math.ceil(closeMin / 60);
                        const slotMinutes = Number(draft.durationMinutes || service.appointmentDurationMins) || 30;
                        const bookedRanges = filteredBookings
                          .filter((booking) => bookingIncludesService(booking, service.serviceUuid))
                          .filter((booking) => String(booking.status || '').toLowerCase() !== 'cancelled')
                          .map((booking) => {
                            const start = parseSlotDateTime(booking.appointmentStartTime);
                            const end = parseSlotDateTime(booking.appointmentEndTime);
                            return start && end ? { start, end } : null;
                          })
                          .filter(Boolean);
                        timeline = {
                          openHour,
                          closeHour,
                          slotMinutes,
                          bookedSlots: bookedRanges,
                          slotsTotal: serviceSlotSummary?.slotsTotal ?? serviceSlots.length,
                          booked: bookedCount,
                          open: openCount,
                        };
                      }
                      return (
                        <ServiceTileSummary
                          openTime={openTimeRaw}
                          closeTime={closeTimeRaw}
                          lunchBreak={draft.extraField || service.lunchBreak || null}
                          activeDays={activeDays}
                          timeline={timeline}
                        />
                      );
                    })()}

                    {isServiceEditing && (
                    <>
                    <div className="appointments-v2-service-fields-row">
                      <div className="appointments-v2-field">
                        <label className="appointments-v2-field-label">Duration</label>
                        <select
                          className="appointments-v2-select"
                          value={draft.durationMinutes ?? 30}
                          onChange={(event) => setServiceDrafts((previous) => ({
                            ...previous,
                            [service.serviceUuid]: { ...(previous[service.serviceUuid] || {}), durationMinutes: Number(event.target.value) }
                          }))}
                          disabled={!isServiceEditing}
                        >
                          <option value="15">15 min</option>
                          <option value="30">30 min</option>
                          <option value="45">45 min</option>
                          <option value="60">60 min</option>
                          <option value="90">90 min</option>
                        </select>
                      </div>
                      <div className="appointments-v2-field">
                        <label className="appointments-v2-field-label">Bookings per slot</label>
                        <select
                          className="appointments-v2-select"
                          value={draft.capacityPerSlot ?? 1}
                          onChange={(event) => setServiceDrafts((previous) => ({
                            ...previous,
                            [service.serviceUuid]: { ...(previous[service.serviceUuid] || {}), capacityPerSlot: Number(event.target.value) }
                          }))}
                          disabled={!isServiceEditing}
                        >
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                        </select>
                      </div>
                      <div className="appointments-v2-field">
                        <label className="appointments-v2-field-label">Price</label>
                        <input
                          className="appointments-v2-input"
                          value={draft.price ?? ''}
                          onChange={(event) => setServiceDrafts((previous) => ({
                            ...previous,
                            [service.serviceUuid]: { ...(previous[service.serviceUuid] || {}), price: event.target.value }
                          }))}
                          disabled={!isServiceEditing}
                        />
                      </div>
                    </div>

                    <div className="appointments-v2-service-fields-row">
                      <div className="appointments-v2-field">
                        <label className="appointments-v2-field-label">Opens at</label>
                        <TimePickerControl
                          value={draft.openTime ?? ''}
                          onChange={(event) => handleServiceTimeDraftChange(service, 'openTime', event.target.value)}
                          ariaLabel={`${service.serviceName || 'Service'} opening time`}
                          disabled={!isServiceEditing}
                        />
                      </div>
                      <div className="appointments-v2-field">
                        <label className="appointments-v2-field-label">Closes at</label>
                        <TimePickerControl
                          value={draft.closeTime ?? ''}
                          onChange={(event) => handleServiceTimeDraftChange(service, 'closeTime', event.target.value)}
                          ariaLabel={`${service.serviceName || 'Service'} closing time`}
                          disabled={!isServiceEditing}
                        />
                      </div>
                      <div className="appointments-v2-field">
                        <label className="appointments-v2-field-label">
                          {isVirtualService ? 'Deposit' : 'Lunch break'}
                        </label>
                        {isVirtualService ? (
                          <input
                            className="appointments-v2-input"
                            value={draft.extraField ?? ''}
                            onChange={(event) => setServiceDrafts((previous) => ({
                              ...previous,
                              [service.serviceUuid]: { ...(previous[service.serviceUuid] || {}), extraField: event.target.value }
                            }))}
                            placeholder="₹ amount"
                            disabled={!isServiceEditing}
                          />
                        ) : (
                          <TimePickerControl
                            value={draft.extraField ?? ''}
                            onChange={(event) => setServiceDrafts((previous) => ({
                              ...previous,
                              [service.serviceUuid]: { ...(previous[service.serviceUuid] || {}), extraField: event.target.value }
                            }))}
                            ariaLabel={`${service.serviceName || 'Service'} lunch break time`}
                            disabled={!isServiceEditing}
                          />
                        )}
                      </div>
                    </div>

                    <div className="appointments-v2-field appointments-v2-operating-days-field">
                      <label className="appointments-v2-field-label">Operating days</label>
                      <div className="appointments-v2-day-row appointments-v2-day-row-operating">
                        {WEEKDAY_CHIPS.map((chip) => {
                          const active = activeDays.includes(chip.index);
                          return (
                            <button
                              type="button"
                              key={`${service.serviceUuid}-${chip.key}`}
                              className={`appointments-v2-day-chip ${active ? 'active' : ''}`}
                              disabled={!isServiceEditing}
                              onClick={() => setServiceDrafts((previous) => {
                                if (!isServiceEditing) {
                                  return previous;
                                }
                                const currentDraft = previous[service.serviceUuid] || {};
                                const currentDays = Array.isArray(currentDraft.activeDays)
                                  ? currentDraft.activeDays
                                  : WEEKDAY_CHIPS.map((dayChip) => dayChip.index);
                                const nextDays = currentDays.includes(chip.index)
                                  ? currentDays.filter((day) => day !== chip.index)
                                  : [...currentDays, chip.index];
                                return {
                                  ...previous,
                                  [service.serviceUuid]: {
                                    ...currentDraft,
                                    activeDays: nextDays
                                  }
                                };
                              })}
                            >
                              {chip.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="appointments-v2-holds-section">
                      <div className="appointments-v2-holds-head">
                        <label className="appointments-v2-field-label">Schedule holds</label>
                        {isServiceEditing && !hasHoldEditor && (
                          <button
                            type="button"
                            className="appointments-v2-btn appointments-v2-btn-primary appointments-v2-btn-tiny"
                            onClick={() => openServiceHoldEditor(service.serviceUuid)}
                          >
                            + Add hold
                          </button>
                        )}
                      </div>
                      <p className="appointments-v2-holds-help">
                        Holds block today only and must stay inside this service&apos;s appointment hours.
                      </p>

                      {scheduleHoldGroups.length > 0 ? (
                        <div className="appointments-v2-holds-list">
                          {scheduleHoldGroups.map((holdGroup) => (
                            <div key={`${service.serviceUuid}-${holdGroup.key}`} className="appointments-v2-hold-group">
                              <div className="appointments-v2-hold-group-head">
                                <strong className="appointments-v2-hold-group-title">{holdGroup.label}</strong>
                                <span className="appointments-v2-hold-group-count">{`${holdGroup.items.length} hold${holdGroup.items.length > 1 ? 's' : ''}`}</span>
                              </div>
                              <div className="appointments-v2-hold-group-list">
                                {holdGroup.items.map((hold) => (
                                  <div key={hold.id} className="appointments-v2-hold-item">
                                    <div className="appointments-v2-hold-copy">
                                      <div className="appointments-v2-hold-meta-row">
                                        <span className="appointments-v2-hold-meta-label">Service unavailable:</span>
                                        <span className="appointments-v2-hold-meta-value">{formatScheduleHoldTimeDetail(hold)}</span>
                                      </div>
                                      <div className="appointments-v2-hold-meta-row">
                                        <span className="appointments-v2-hold-meta-label">Reason:</span>
                                        <span className="appointments-v2-hold-meta-value">{hold.reason || 'No reason added'}</span>
                                      </div>
                                    </div>
                                    {isServiceEditing && (
                                      <button
                                        type="button"
                                        className="appointments-v2-btn appointments-v2-btn-ghost appointments-v2-btn-tiny"
                                        onClick={() => removeServiceHoldFromDraft(service.serviceUuid, hold.id)}
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="appointments-v2-holds-empty">No holds configured</p>
                      )}

                      {hasHoldEditor && (
                        <div className="appointments-v2-hold-editor">
                          <div className="appointments-v2-service-fields-row appointments-v2-hold-editor-grid">
                            <div className="appointments-v2-field">
                              <label className="appointments-v2-field-label">Date</label>
                              <input
                                className="appointments-v2-input"
                                value={getTodayDateKey()}
                                readOnly
                              />
                            </div>
                            <div className="appointments-v2-field">
                              <label className="appointments-v2-field-label">From</label>
                              <TimePickerControl
                                value={holdEditor?.fromTime || ''}
                                onChange={(event) => updateServiceHoldEditor(service.serviceUuid, {
                                  type: 'time_window',
                                  scope: 'date',
                                  date: getTodayDateKey(),
                                  fromTime: event.target.value
                                })}
                                ariaLabel={`${service.serviceName || 'Service'} hold from time`}
                              />
                            </div>
                            <div className="appointments-v2-field">
                              <label className="appointments-v2-field-label">To</label>
                              <TimePickerControl
                                value={holdEditor?.toTime || ''}
                                onChange={(event) => updateServiceHoldEditor(service.serviceUuid, {
                                  type: 'time_window',
                                  scope: 'date',
                                  date: getTodayDateKey(),
                                  toTime: event.target.value
                                })}
                                ariaLabel={`${service.serviceName || 'Service'} hold to time`}
                              />
                            </div>
                            <div className="appointments-v2-field appointments-v2-field-span-2">
                              <label className="appointments-v2-field-label">Reason</label>
                              <input
                                className="appointments-v2-input"
                                value={holdEditor?.reason || ''}
                                placeholder="Reason (optional, max 50 chars)"
                                maxLength={SERVICE_HOLD_REASON_MAX_LENGTH}
                                onChange={(event) => updateServiceHoldEditor(service.serviceUuid, { reason: normalizeHoldReason(event.target.value) })}
                              />
                            </div>
                          </div>

                          {!!holdEditorValidationError && (
                            <p className="appointments-v2-holds-error">{holdEditorValidationError}</p>
                          )}

                          <div className="appointments-v2-inline-actions appointments-v2-hold-editor-actions">
                            <button
                              type="button"
                              className="appointments-v2-btn appointments-v2-btn-ghost appointments-v2-btn-tiny"
                              onClick={() => closeServiceHoldEditor(service.serviceUuid)}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="appointments-v2-btn appointments-v2-btn-primary appointments-v2-btn-tiny"
                              onClick={() => addServiceHoldToDraft(service)}
                              disabled={Boolean(holdEditorValidationError)}
                            >
                              Add hold
                            </button>
                          </div>
                        </div>
                      )}

                      {hasInvalidServiceDraft && (
                        <p className="appointments-v2-holds-error">{serviceValidationError}</p>
                      )}
                    </div>

                    {false && (
                    <div className="appointments-v2-slot-preview">
                      <span className="appointments-v2-slot-preview-label">Today&apos;s slots</span>
                      <div className="appointments-v2-slot-preview-row">
                        {slotPreviewView.slotChips.map((slotChip) => {
                          const isSelectedSlot = selectedServiceSlot?.serviceUuid === service.serviceUuid
                            && selectedServiceSlot?.slot?.slotStartTime === slotChip.slot?.slotStartTime
                            && selectedServiceSlot?.slot?.slotEndTime === slotChip.slot?.slotEndTime;
                          return (
                            <button
                              type="button"
                              key={slotChip.key}
                              className={`appointments-v2-preview-chip appointments-v2-preview-chip-button ${slotChip.tone} ${isSelectedSlot ? 'selected' : ''}`}
                              data-slot-status={slotChip.status}
                              aria-pressed={isSelectedSlot}
                              onClick={() => setSelectedServiceSlot({
                                serviceUuid: service.serviceUuid,
                                serviceName: service.serviceName || 'Service',
                                capacityPerSlot: service.capacityPerSlot,
                                maxBookingsPerSlot: service.maxBookingsPerSlot,
                                slot: slotChip.slot
                              })}
                            >
                              {slotChip.label}
                            </button>
                          );
                        })}
                        {slotPreviewView.isEmpty && (
                          <span className="appointments-v2-preview-chip muted">No slots</span>
                        )}
                      </div>
                    </div>
                    )}
                    </>
                    )}

                    {isServiceEditing && (
                    <div className="appointments-v2-service-footer">
                      <span>
                        {slotPreviewView.summaryView
                          ? slotPreviewView.summaryView.footer
                          : isActive
                          ? `${serviceSlots.length} slots · ${bookedCount} booked · ${openCount} open`
                          : 'Disabled — no slots generated today'}
                      </span>
                      {isServiceEditing ? (
                        <div className="appointments-v2-service-footer-actions">
                          <button
                            type="button"
                            className="appointments-v2-btn appointments-v2-btn-danger-outline appointments-v2-btn-tiny"
                            onClick={() => setDeleteServiceTarget(service)}
                            disabled={disableServiceSave}
                            title={disableServiceSave ? 'Finish or cancel pending changes before deleting.' : undefined}
                          >
                            Delete
                          </button>
                          <div className="appointments-v2-inline-actions">
                            <button
                              type="button"
                              className="appointments-v2-btn appointments-v2-btn-ghost appointments-v2-btn-tiny"
                              onClick={() => cancelServiceCardEdit(service)}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="appointments-v2-btn appointments-v2-btn-primary appointments-v2-btn-tiny"
                              onClick={() => saveServiceCardEdit(service)}
                              disabled={disableServiceSave}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    )}
                  </div>
                );
              })}

            </div>

            {selectedSlotDrilldown && (
              <div className="appointments-v2-slot-drilldown" aria-live="polite" ref={slotDetailPanelRef}>
                <div className="appointments-v2-slot-drilldown-head">
                  <div>
                    <span className="appointments-v2-slot-drilldown-kicker">Selected slot</span>
                    <h4>{`${selectedSlotDrilldown.serviceName} · ${selectedSlotDrilldown.slotLabel}`}</h4>
                    <p>{`${selectedSlotDrilldown.bookingCount} appointment${selectedSlotDrilldown.bookingCount === 1 ? '' : 's'} in this slot`}</p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {/* F42: Delete Slot button (only for today's slots) */}
                    {selectedDateKey === formatDateInput(new Date()) && selectedServiceSlot?.slot && (
                      <button
                        type="button"
                        className="appointments-v2-btn appointments-v2-btn-ghost appointments-v2-btn-tiny"
                        style={{ color: '#EF4444' }}
                        onClick={() => {
                          // Extract time in HH:mm format from slot datetime
                          const extractTime = (datetime) => {
                            const parsed = parseDateValue(datetime);
                            if (!parsed) return '--';
                            const hours = pad(parsed.getHours());
                            const minutes = pad(parsed.getMinutes());
                            return `${hours}:${minutes}`;
                          };

                          setSlotToDelete({
                            serviceUuid: selectedSlotDrilldown.serviceUuid,
                            serviceName: selectedSlotDrilldown.serviceName,
                            date: selectedDateKey,
                            startTime: extractTime(selectedServiceSlot.slot.slotStartTime),
                            endTime: extractTime(selectedServiceSlot.slot.slotEndTime),
                            displayTime: `${selectedSlotDrilldown.startLabel} - ${selectedSlotDrilldown.endLabel}`
                          });
                          setDeleteSlotModalOpen(true);
                        }}
                      >
                        Delete Slot
                      </button>
                    )}
                    <button
                      type="button"
                      className="appointments-v2-btn appointments-v2-btn-ghost appointments-v2-btn-tiny"
                      onClick={() => setSelectedServiceSlot(null)}
                    >
                      Close
                    </button>
                  </div>
                </div>

                {selectedSlotDrilldown.isEmpty ? (
                  <div className="appointments-v2-slot-drilldown-empty">
                    No appointments booked in this slot.
                  </div>
                ) : (
                  <div className="appointments-v2-slot-drilldown-list">
                    {selectedSlotDrilldown.bookings.map((booking) => {
                      const isWalkin = booking.walkin === true;
                      const bookingSourceBadge = isWalkin
                        ? { label: 'Walk-in', className: 'booking-source-badge-walkin' }
                        : { label: 'App', className: 'booking-source-badge-app' };

                      return (
                        <div
                          className="appointments-v2-slot-drilldown-booking"
                          key={booking.key}
                          role="button"
                          tabIndex={0}
                          onClick={() => openAppointmentDetails(booking)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openAppointmentDetails(booking);
                            }
                          }}
                        >
                          <div className="appointments-v2-slot-drilldown-booking-main">
                            <div className="appointments-v2-booking-header">
                              <strong>{booking.customerName}</strong>
                              <span className={`booking-source-badge ${bookingSourceBadge.className}`}>
                                {bookingSourceBadge.label}
                              </span>
                            </div>
                            <span>{booking.customerPhone || 'No phone added'}</span>
                            <AppointmentOfferChip booking={booking} />
                            <AppointmentMembershipChip booking={booking} />
                            <AppointmentLoyaltyRewardChip booking={booking} />
                            {booking.notes && <p>{booking.notes}</p>}

                            {/* F45: Walk-in action buttons */}
                            {isWalkin && !['done', 'cancelled', 'missed'].includes(booking.status) && (
                              <div className="appointments-v2-booking-actions">
                                <button
                                  className="appointments-v2-btn appointments-v2-btn-small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleEditWalkinClick(booking);
                                  }}
                                  type="button"
                                >
                                  <Edit2 size={14} /> Edit
                                </button>
                                <button
                                  className="appointments-v2-btn appointments-v2-btn-small"
                                  disabled={isWalkinOfferMutating}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (booking.offerId != null || booking.offerCode) {
                                      detachOfferFromWalkinBooking(booking);
                                    } else if (booking.loyaltyRewardUuid) {
                                      detachLoyaltyRewardFromWalkinBooking(booking);
                                    } else if (booking.membershipUuid) {
                                      detachMembershipFromWalkinBooking(booking);
                                    } else {
                                      openWalkinOfferPicker(booking);
                                    }
                                  }}
                                  type="button"
                                >
                                  <Tag size={14} /> {booking.offerId != null || booking.offerCode
                                    ? 'Remove offer'
                                    : booking.loyaltyRewardUuid ? 'Remove reward'
                                      : booking.membershipUuid ? 'Remove membership' : 'Benefit'}
                                </button>
                                <button
                                  className="appointments-v2-btn appointments-v2-btn-small appointments-v2-btn-danger"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setDeleteWalkinTarget(booking);
                                  }}
                                  type="button"
                                >
                                  <Trash2 size={14} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="appointments-v2-slot-drilldown-booking-meta">
                            <span>{booking.timeLabel}</span>
                            <span className={`appointments-v2-slot-drilldown-status ${booking.status}`}>
                              {booking.statusLabel}
                            </span>
                            <select
                              className="appointments-v2-appt-status-select"
                              value={booking.status}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(e) => handleStatusChange(booking, e.target.value)}
                              disabled={isPastDate}
                              title={isPastDate ? 'Status cannot be updated for past dates' : undefined}
                            >
                              {['pending', 'confirmed', 'in_progress', 'done', 'missed', 'cancelled'].map(s => {
                                const allowed = BOOKING_STATUS_TRANSITIONS[booking.status] || [];
                                return (
                                  <option key={s} value={s} disabled={s !== booking.status && !allowed.includes(s)}>
                                    {statusBadgeLabel(s)}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </article>
          </ServiceManager>
        </section>
      </AppointmentSettings>

      {showServiceEditor && (
        <div className="zone-modal-overlay" onClick={closeServiceEditor}>
          <div className="zone-modal appointments-v2-service-modal" onClick={(event) => event.stopPropagation()}>
            <div className="zone-modal-header">
              <h3>{editingServiceUuid ? 'Edit service' : 'Add service'}</h3>
            </div>
            <div className="zone-modal-body two-col">
              <div className="manage-input-group full-width">
                <label>Service name</label>
                <input className="appointments-v2-input" value={serviceForm.serviceName} onChange={(event) => setServiceForm((previous) => ({ ...previous, serviceName: event.target.value }))} />
              </div>
              <div className="manage-input-group full-width">
                <label>Description</label>
                <textarea className="appointments-v2-textarea" value={serviceForm.serviceDescription} onChange={(event) => setServiceForm((previous) => ({ ...previous, serviceDescription: event.target.value }))} />
              </div>
              <div className="manage-input-group">
                <label>Mode</label>
                <select className="appointments-v2-select" value={serviceForm.appointmentMode} onChange={(event) => setServiceForm((previous) => ({ ...previous, appointmentMode: event.target.value }))}>
                  <option value="IN_STORE">In-store</option>
                  <option value="VIRTUAL">Virtual</option>
                  <option value="HYBRID">Hybrid</option>
                </select>
              </div>
              <div className="manage-input-group">
                <label>Duration</label>
                <input type="number" min="5" className="appointments-v2-input" value={serviceForm.appointmentDurationMins} onChange={(event) => setServiceForm((previous) => ({ ...previous, appointmentDurationMins: Number(event.target.value) || 30 }))} />
              </div>
              <div className="manage-input-group">
                <label>Capacity per slot</label>
                <input type="number" min="1" className="appointments-v2-input" value={serviceForm.maxBookingsPerSlot} onChange={(event) => setServiceForm((previous) => ({ ...previous, maxBookingsPerSlot: Number(event.target.value) || 1 }))} />
              </div>
              <div className="manage-input-group">
                <label>Price / deposit</label>
                <input className="appointments-v2-input" value={serviceForm.depositAmount} onChange={(event) => setServiceForm((previous) => ({ ...previous, depositAmount: event.target.value }))} />
              </div>
            </div>
            <div className="zone-modal-actions">
              <button type="button" className="appointments-v2-btn appointments-v2-btn-ghost" onClick={closeServiceEditor} disabled={isSavingService}>Cancel</button>
              <button type="button" className="appointments-v2-btn appointments-v2-btn-primary" onClick={saveService} disabled={isSavingService}>
                {isSavingService ? 'Saving…' : (editingServiceUuid ? 'Update service' : 'Add service')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteServiceTarget && (
        <ConfirmDialog
          open={Boolean(deleteServiceTarget)}
          title="Delete service?"
          description={(
            <p>
              This will remove <strong>{deleteServiceTarget?.serviceName || 'this service'}</strong> from your services list. This action cannot be undone.
            </p>
          )}
          confirmLabel={isDeletingService ? 'Deleting...' : 'Delete Service'}
          confirmVariant="danger"
          busy={isDeletingService}
          onCancel={() => setDeleteServiceTarget(null)}
          onConfirm={confirmDeleteService}
        />
      )}

      {isGlobalLoading && (
        <div className="global-request-overlay" aria-live="polite" aria-label="Syncing data">
          <div className="global-request-loader">
            <span className="global-request-spinner" />
            <p>Syncing data...</p>
          </div>
        </div>
      )}

      {isHistoryModalOpen ? (
        <div className="zone-modal-overlay" onClick={() => !isDownloadingHistory && setIsHistoryModalOpen(false)}>
          <div
            className="zone-modal appointments-v2-history-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Download appointment history"
          >
            <div className="zone-modal-header">
              <h3>Download appointment history</h3>
              <button
                type="button"
                className="appointments-v2-history-close"
                onClick={() => setIsHistoryModalOpen(false)}
                disabled={isDownloadingHistory}
                aria-label="Close"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="zone-modal-body appointments-v2-history-body">
              <p className="appointments-v2-history-hint">
                Export bookings as a CSV. History is available for the last 6 months.
              </p>
              <div className="appointments-v2-history-field">
                <label htmlFor="appointment-history-start">Start date</label>
                <input
                  id="appointment-history-start"
                  type="date"
                  value={historyStartDate}
                  min={historyMinDate}
                  max={historyEndDate || todayIso}
                  onChange={(event) => setHistoryStartDate(event.target.value)}
                  disabled={isDownloadingHistory}
                />
              </div>
              <div className="appointments-v2-history-field">
                <label htmlFor="appointment-history-end">End date</label>
                <input
                  id="appointment-history-end"
                  type="date"
                  value={historyEndDate}
                  min={historyStartDate || historyMinDate}
                  max={todayIso}
                  onChange={(event) => setHistoryEndDate(event.target.value)}
                  disabled={isDownloadingHistory}
                />
              </div>
              {historyStartDate && historyEndDate && historyStartDate > historyEndDate ? (
                <p className="appointments-v2-history-error">Start date cannot be after end date.</p>
              ) : null}
            </div>
            <div className="zone-modal-actions">
              <button
                type="button"
                className="btn ghost small"
                onClick={() => setIsHistoryModalOpen(false)}
                disabled={isDownloadingHistory}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn primary small"
                onClick={handleDownloadHistory}
                disabled={
                  isDownloadingHistory ||
                  !historyStartDate ||
                  !historyEndDate ||
                  historyStartDate > historyEndDate
                }
              >
                {isDownloadingHistory ? 'Preparing…' : 'Download CSV'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <WalkinBookingModal
        open={walkinModalOpen}
        onClose={() => setWalkinModalOpen(false)}
        services={services}
        selectedDate={selectedDate}
        settings={settings}
        onSubmit={handleCreateWalkin}
        isLoading={isCreatingWalkin}
      />

      {/* F42: Delete Slot Modal */}
      <DeleteSlotModal
        open={deleteSlotModalOpen}
        onClose={() => {
          setDeleteSlotModalOpen(false);
          setSlotToDelete(null);
        }}
        slot={slotToDelete}
        affectedBookings={
          selectedSlotDrilldown?.bookings?.map(booking => ({
            customerName: booking.customerName,
            customerPhone: booking.customerPhone,
            bookingUuid: booking.bookingUuid
          })) || []
        }
        onConfirm={handleDeleteSlot}
        isDeleting={isDeletingSlot}
      />

      {/* W3 — walk-in offer picker ("I saw this offer in the app") */}
      {offerPickerBooking && (
        <div
          className="zone-modal-overlay"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setOfferPickerBooking(null);
            }
          }}
        >
          <div
            className="zone-modal live-v2-offer-picker-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Attach benefit to ${offerPickerBooking.customerName || 'walk-in'}`}
          >
            <div className="zone-modal-header">
              <div>
                <h3>Attach benefit</h3>
                <p>{`Walk-in · ${offerPickerBooking.customerName || 'Customer'}`}</p>
              </div>
              <button type="button" className="btn ghost" onClick={() => setOfferPickerBooking(null)}>
                Close
              </button>
            </div>
            <div className="live-v2-offer-picker-body">
              {walkinPickerMembership ? (
                <button
                  type="button"
                  className={`live-v2-offer-picker-item live-v2-membership-item${walkinPickerMembership.attached ? ' ineligible' : ''}`}
                  disabled={isWalkinOfferMutating || walkinPickerMembership.attached}
                  onClick={attachMembershipToWalkinBooking}
                >
                  <span className="live-v2-offer-picker-item-main">
                    <strong>{walkinPickerMembership.label || 'Membership benefit'}</strong>
                    <small>
                      {walkinPickerMembership.valuePaise > 0
                        ? `Saves ${formatCurrencyFromPaise(walkinPickerMembership.valuePaise)} on this visit`
                        : 'Member perks — does not block offers'}
                    </small>
                  </span>
                  <span className={`live-v2-offer-picker-chip ${walkinPickerMembership.attached ? 'ineligible' : 'eligible'}`}>
                    {walkinPickerMembership.attached ? 'Attached' : 'Member'}
                  </span>
                </button>
              ) : null}
              {walkinPickerLoyaltyReward ? (
                <button
                  type="button"
                  className={`live-v2-offer-picker-item live-v2-membership-item${walkinPickerLoyaltyReward.attached ? ' ineligible' : ''}`}
                  disabled={isWalkinOfferMutating || walkinPickerLoyaltyReward.attached}
                  onClick={attachLoyaltyRewardToWalkinBooking}
                >
                  <span className="live-v2-offer-picker-item-main">
                    <strong>{walkinPickerLoyaltyReward.label || 'Loyalty reward'}</strong>
                    <small>
                      {walkinPickerLoyaltyReward.valuePaise > 0
                        ? `Saves ${formatCurrencyFromPaise(walkinPickerLoyaltyReward.valuePaise)} on this visit`
                        : 'Earned loyalty reward'}
                    </small>
                  </span>
                  <span className={`live-v2-offer-picker-chip ${walkinPickerLoyaltyReward.attached ? 'ineligible' : 'eligible'}`}>
                    {walkinPickerLoyaltyReward.attached ? 'Attached' : 'Reward'}
                  </span>
                </button>
              ) : null}
              <input
                className="live-v2-offer-picker-search"
                type="search"
                placeholder="Search live offers by title or coupon code"
                value={walkinPickerSearch}
                onChange={(event) => setWalkinPickerSearch(event.target.value)}
                autoFocus
              />
              {walkinPickerLoading ? (
                <div className="live-v2-offer-picker-loading">
                  <Skeleton height={44} width="100%" />
                  <Skeleton height={44} width="100%" />
                </div>
              ) : visibleWalkinPickerOffers.length === 0 ? (
                <p className="live-v2-offer-picker-empty">
                  {walkinPickerOffers.length === 0
                    ? 'No live offers right now — publish one from the Offers tab first.'
                    : 'No live offers match this search.'}
                </p>
              ) : (
                <ul className="live-v2-offer-picker-list">
                  {visibleWalkinPickerOffers.map((offer) => (
                    <li key={offer.id}>
                      <button
                        type="button"
                        className={`live-v2-offer-picker-item${offer.eligible === false ? ' ineligible' : ''}`}
                        disabled={isWalkinOfferMutating || offer.eligible === false}
                        title={offer.eligible === false ? (offer.reason || 'Not eligible for this customer') : undefined}
                        onClick={() => attachOfferToWalkinBooking(offer)}
                      >
                        <span className="live-v2-offer-picker-item-main">
                          <strong>{offer.title || 'Untitled offer'}</strong>
                          <small>
                            {offer.discount || ''}
                            {offer.couponCode ? ` · ${offer.couponCode}` : ''}
                          </small>
                          {offer.eligible === false && offer.reason && (
                            <small className="live-v2-offer-picker-item-reason">{offer.reason}</small>
                          )}
                        </span>
                        <span className={`live-v2-offer-picker-chip ${offer.eligible === false ? 'ineligible' : 'eligible'}`}>
                          {offer.eligible === false ? 'Not eligible' : 'Eligible'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* F45: Edit Walkin Booking Modal */}
      {editWalkinModalOpen && (
        <EditWalkinBookingModal
          booking={editingBooking}
          open={editWalkinModalOpen}
          onClose={() => {
            setEditWalkinModalOpen(false);
            setEditingBooking(null);
          }}
          services={services}
          settings={settings}
          onSubmit={handleEditWalkinSubmit}
          isLoading={isCreatingWalkin}
        />
      )}

      {/* F45: Delete Walkin Booking Confirmation */}
      {confirmDoneBooking && (
        <ConfirmDialog
          open={Boolean(confirmDoneBooking)}
          title="Mark this visit as done?"
          description={
            <p>
              Once <strong>{confirmDoneBooking?.customerName || 'this visit'}</strong> is marked done,
              services and offers can no longer be added or changed, and the final visit summary
              will be generated.
            </p>
          }
          confirmLabel={isMarkingDone ? 'Marking done...' : 'Mark as done'}
          busy={isMarkingDone}
          onConfirm={confirmMarkDone}
          onCancel={() => setConfirmDoneBooking(null)}
        />
      )}

      {deleteWalkinTarget && (
        <ConfirmDialog
          open={Boolean(deleteWalkinTarget)}
          title="Delete walk-in booking?"
          description={
            <p>
              Remove booking for <strong>{deleteWalkinTarget?.customerName}</strong>?
              <br />
              This action cannot be undone.
            </p>
          }
          confirmLabel={isDeletingWalkin ? 'Deleting...' : 'Delete Booking'}
          confirmVariant="danger"
          onConfirm={confirmDeleteWalkin}
          onCancel={() => setDeleteWalkinTarget(null)}
        />
      )}

      <AppointmentDetailsModal
        isOpen={isAppointmentDetailsOpen}
        details={selectedAppointmentDetails}
        summary={selectedAppointmentSummary}
        isLoading={isLoadingAppointmentDetails}
        onClose={closeAppointmentDetails}
      />

      <ConfirmDialog
        open={Boolean(arrivalOtpDialogBooking)}
        title="Verify arrival OTP"
        description={(
          <div style={{ display: 'grid', gap: '12px' }}>
            <p style={{ margin: 0 }}>
              Enter the 6-digit OTP shared by {arrivalOtpDialogBooking?.customerName || 'the customer'} to move this booking to In Progress.
            </p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={arrivalOtpValue}
              onChange={(event) => setArrivalOtpValue(event.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter OTP"
              aria-label="Arrival OTP"
              disabled={isVerifyingArrivalOtp}
              style={{
                width: '100%',
                borderRadius: '12px',
                border: '1px solid var(--trae-border, #d4d4d8)',
                padding: '12px 14px',
                fontSize: '16px'
              }}
            />
          </div>
        )}
        confirmLabel={isVerifyingArrivalOtp ? 'Verifying...' : 'Verify arrival'}
        cancelLabel="Close"
        busy={isVerifyingArrivalOtp}
        onConfirm={confirmArrivalOtp}
        onCancel={closeArrivalOtpDialog}
      />
    </div>
    </ErrorBoundary>
  );
};

export default StoreAppointments;
